<template>
   <div class="client-dashboard-page">
      <!-- ── Top Organisation Card ── -->
      <div class="org-header-card card border-0 shadow-sm mb-4 p-4">
         <div class="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <!-- Left: Avatar, Name, Badges, Subtitle -->
            <div class="d-flex align-items-center gap-3">
               <div class="org-avatar-box d-flex align-items-center justify-content-center">
                  <i class="fas fa-building"></i>
               </div>
               <div>
                  <div class="d-flex align-items-center flex-wrap gap-2 mb-1">
                     <h2 class="org-header-title mb-0">{{ profileData.name || 'Loading...' }}</h2>
                     <div class="badge-container d-inline-flex align-items-center gap-1">
                        <span v-for="c in profileData.classifications" :key="c" class="org-pill-badge" :class="'org-pill-badge--' + c.toLowerCase()">
                           {{ c }}
                        </span>
                        <span v-if="getVertexLeadName()" class="org-pill-badge org-pill-badge--owner" :title="'Owner (Vertex Lead): ' + getVertexLeadName()">
                           <i class="fas fa-user-tie me-1"></i> Vertex Lead: {{ getVertexLeadName() }}
                        </span>
                        <span v-if="profileData.is_merged" class="org-pill-badge org-pill-badge--merged" :title="'Merged into: ' + (profileData.merged_into_name || 'Primary Organisation')">
                           <i class="fas fa-code-branch me-1"></i> Merged to {{ profileData.merged_into_name || 'Primary Organisation' }}
                        </span>
                        <span v-if="profileData.isTestData" class="org-pill-badge org-pill-badge--test">Test</span>
                        <span v-if="profileData.isAuthSynced" class="org-pill-badge org-pill-badge--synced" title="Synced with Auth Portal">
                           <i class="fas fa-sync-alt me-1"></i> Auth Synced
                        </span>
                        <span v-else-if="profileData.hasAuthOrganisation == 1" class="org-pill-badge org-pill-badge--linked" :title="'Manually linked to Auth: ' + (profileData.authOrganisationName || 'Auth Org #' + profileData.authOrganisationId)">
                           <i class="fas fa-link me-1"></i> Manually Linked to {{ profileData.authOrganisationName || ('Auth Org #' + profileData.authOrganisationId) }}
                        </span>
                        <span v-else-if="profileData.hasAuthOrganisation == 2" class="org-pill-badge org-pill-badge--marked" title="Marked for creation in Auth">
                           <i class="fas fa-clock me-1"></i> Marked for Creation
                        </span>
                     </div>
                  </div>
                  <div class="org-header-subtitle text-muted small d-flex align-items-center gap-2 flex-wrap">
                     <span>{{ profileData.abn ? profileData.abn : (profileData.phone ? profileData.phone : 'No ABN Provided') }}</span>
                     <span v-if="getDisplayFormerNames()" class="text-secondary d-inline-flex align-items-center">
                        <span class="text-muted me-1">&bull; <i class="fa-solid fa-history me-1"></i>Formerly:</span>
                        <strong class="text-dark">{{ getDisplayFormerNames() }}</strong>
                     </span>
                  </div>
               </div>
            </div>

            <!-- Right: Search Organisation Bar with Total Count & Back Button -->
            <div class="org-switcher-box">
               <label class="org-switcher-title mb-1">Switch Organisation ({{ leads.length }})</label>
               <div class="d-flex align-items-center gap-2">
                  <router-link 
                     to="/clients/view-clients" 
                     class="btn btn-outline-secondary back-to-all-btn d-inline-flex align-items-center justify-content-center flex-shrink-0" 
                     title="Back to All Organisations"
                     style="border-radius: 8px; border: 1.5px solid #cbd5e1; height: 38px; width: 38px; color: #475569; font-weight: 600; padding: 0; background-color: #fff; text-decoration: none;"
                  >
                     <i class="fas fa-arrow-left"></i>
                  </router-link>
                  <div class="header-search-container m-0">
                     <div class="header-search-input-wrapper">
                        <i class="fas fa-search search-icon"></i>
                        <input 
                           type="text" 
                           v-model="searchQuery" 
                           class="header-search-input" 
                           placeholder="Search across all organisations..." 
                           @focus="showSearchDropdown = true"
                           @input="onSearchInput"
                        />
                        <button v-if="searchQuery" class="clear-search-btn" @click="clearSearchQuery" type="button">
                           <i class="fas fa-times"></i>
                        </button>
                     </div>
                     
                     <!-- Suggestions Dropdown overlay -->
                     <div v-if="showSearchDropdown && searchQuery && searchDropdownList.length > 0" class="header-search-dropdown">
                        <div 
                           v-for="org in searchDropdownList" 
                           :key="org.id || org.uuid" 
                           class="header-search-dropdown-item"
                           @mousedown="selectOrgFromDropdown(org)"
                        >
                           <div class="d-flex align-items-center justify-content-between">
                              <div>
                                 <span class="org-name-text">{{ org.organisation_name }}</span>
                                 <div v-if="org.former_names && org.former_names.length" class="text-muted small fst-italic" style="font-size: 0.72rem;">
                                    FKA: {{ org.former_names.join(', ') }}
                                 </div>
                              </div>
                              <div class="d-flex align-items-center gap-1">
                                 <span v-if="org.is_merged" class="org-pill-badge org-pill-badge--merged" style="font-size: 0.65rem; padding: 1px 6px;" :title="'Merged into: ' + (org.merged_into_name || 'Primary Organisation')">
                                    <i class="fas fa-code-branch me-1"></i> Merged to {{ org.merged_into_name || 'Primary Organisation' }}
                                 </span>
                                 <span v-if="isPartner(org) && getOrgClassification(org) !== 'Partner'" class="org-pill-badge org-pill-badge--partner" style="font-size: 0.65rem; padding: 1px 6px;">Partner</span>
                                 <span v-if="isSupplier(org) && getOrgClassification(org) !== 'Supplier'" class="org-pill-badge org-pill-badge--supplier" style="font-size: 0.65rem; padding: 1px 6px;">Supplier</span>
                                 <span class="org-type-badge" :class="'org-type-badge--' + (getOrgClassification(org) || '').toLowerCase()">{{ getOrgClassification(org) }}</span>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </div>

      <!-- ── Merged Organisation Alert Banner ── -->
      <div v-if="profileData.is_merged" class="alert d-flex align-items-center justify-content-between p-3 mb-4 rounded-3 shadow-sm" style="background-color: #f0fdfa; border: 1px solid #99f6e4; border-left: 5px solid #00768a;">
         <div class="d-flex align-items-center gap-3">
            <div class="d-inline-flex align-items-center justify-content-center rounded-circle" style="width: 44px; height: 44px; background-color: rgba(0, 118, 138, 0.12); color: #00768a; flex-shrink: 0;">
               <i class="fas fa-code-branch fa-lg"></i>
            </div>
            <div>
               <h6 class="alert-heading mb-1 fw-bold text-dark">This Organisation Has Been Merged</h6>
               <p class="mb-0 text-muted small">
                  Merged into
                  <a href="javascript:void(0)" @click="navigateToOrganisation(profileData.merged_into_uuid)" class="fw-bold text-decoration-underline" style="color: #00768a; cursor: pointer;">
                     {{ profileData.merged_into_name || 'Primary Organisation' }}
                  </a>
                  <span v-if="profileData.merged_at"> on {{ formatDate(profileData.merged_at) }}</span>
                  <span v-if="profileData.merged_by_name"> by {{ profileData.merged_by_name }}</span>.
                  <span v-if="profileData.merge_reason"><br/><strong>Reason:</strong> {{ profileData.merge_reason }}</span>
               </p>
            </div>
         </div>
         <div class="d-flex align-items-center gap-2">
            <button type="button" class="btn btn-sm btn-vertex-primary" @click="navigateToOrganisation(profileData.merged_into_uuid)">
               <i class="fas fa-external-link-alt me-1"></i> View Primary Organisation
            </button>
            <button type="button" class="btn btn-sm btn-vertex-outline" @click="openUnmergeModal(profileData)">
               <i class="fas fa-undo me-1"></i> Unmerge
            </button>
         </div>
      </div>

      <LoadingPage v-if="isLoading"></LoadingPage>

      <!-- ── Organisations Tabs Card (Matching All Organisations) ── -->
      <div class="ms-card mb-4">
         <div class="org-tabs-container">
            <div class="org-tabs-nav">
               <button 
                  @click="activeTab = 'profile'" 
                  class="org-tab-btn" 
                  :class="{ active: activeTab === 'profile' }"
               >
                  Profile
               </button>
               <button 
                  @click="activeTab = 'contacts'" 
                  class="org-tab-btn" 
                  :class="{ active: activeTab === 'contacts' }"
               >
                  Contacts
                  <span v-if="orgContacts && orgContacts.length" class="org-tab-counter-badge">
                     {{ orgContacts.length }}
                  </span>
               </button>
               <button 
                  @click="activeTab = 'jobs_board'" 
                  class="org-tab-btn" 
                  :class="{ active: activeTab === 'jobs_board' }"
               >
                  Jobs Board
               </button>
               <button 
                  v-if="profileData.classifications && profileData.classifications.includes('Client')" 
                  @click="activeTab = 'client_communication'" 
                  class="org-tab-btn" 
                  :class="{ active: activeTab === 'client_communication' }"
               >
                  Client Communication
               </button>
               <!-- Merged Organisations tab hidden for the meantime -->
               <button 
                  v-if="false"
                  @click="activeTab = 'merged_organisations'" 
                  class="org-tab-btn" 
                  :class="{ active: activeTab === 'merged_organisations' }"
               >
                  Merged Organisations
                  <span v-if="mergedOrganisations && mergedOrganisations.length" class="org-tab-counter-badge">
                     {{ mergedOrganisations.length }}
                  </span>
               </button>
               <button 
                  @click="activeTab = 'timeline'" 
                  class="org-tab-btn" 
                  :class="{ active: activeTab === 'timeline' }"
               >
                  Activity Timeline
               </button>
            </div>

            <!-- Sub-header Meta Row on the right side of the tab bar -->
            <div class="d-flex align-items-center gap-3 flex-wrap py-2 text-muted small" style="font-size: 0.82rem;">
               <div>
                  <span class="text-secondary fw-semibold">Date Created:</span> {{ profileData.firstContactDate || 'Not Available' }}
               </div>
               <span class="text-muted opacity-50">•</span>
               <div>
                  <span class="text-secondary fw-semibold">Last synced:</span> {{ profileData.syncedAt ? formatSyncedDate(profileData.syncedAt) : 'Never' }}
               </div>
               <button 
                  v-if="!profileData.is_merged" 
                  type="button" 
                  class="btn btn-sm btn-vertex-outline d-flex align-items-center gap-1 ms-2" 
                  style="border-radius: 6px; font-size: 0.78rem; font-weight: 600; padding: 4px 10px;"
                  @click="openMergeModal" 
                  title="Merge this organisation into another"
               >
                  <i class="fas fa-code-branch"></i> Merge To...
               </button>
            </div>
         </div>
      </div>

      <!-- ── Tab Content ── -->
      <div class="tab-content-container">
         <!-- PROFILE TAB -->
         <div v-if="activeTab === 'profile'" class="profile-tab-content">
            <!-- 1. Organisation Details Card -->
            <div class="accordion-card card border-0 shadow-sm mb-3">
               <div class="accordion-header d-flex justify-content-between align-items-center p-3" @click="toggleSection('organisationDetails')">
                  <div class="d-flex align-items-center gap-2">
                     <i class="fas fa-building text-info"></i>
                     <span class="accordion-title">Organisation Details</span>
                  </div>

                  <div class="d-flex align-items-center gap-2" @click.stop>
                     <!-- Auth Linkage Status Badge -->
                     <span v-if="profileData.hasAuthOrganisation == 1" class="status-pill status-pill--linked" title="Linked to Auth Portal">
                        <i class="fas fa-check-circle me-1" style="font-size: 0.7rem;"></i> Linked
                     </span>
                     <span v-else-if="profileData.hasAuthOrganisation == 2" class="status-pill status-pill--marked" title="Marked for creation in Auth">
                        <i class="fas fa-clock me-1" style="font-size: 0.7rem;"></i> Marked for Creation
                     </span>
                     <span v-else class="status-pill status-pill--not-linked" title="Not linked to Auth">
                        <i class="fas fa-unlink me-1" style="font-size: 0.7rem;"></i> Not Linked
                     </span>

                     <!-- Platform Action Icons -->
                     <button type="button" 
                             class="btn-accordion-icon btn-acc-auth" 
                             :class="{ 'is-linked': profileData.hasAuthOrganisation == 1 }" 
                             @click.stop="openInAuth"
                             :title="profileData.hasAuthOrganisation == 1 ? 'Open Organisation in Auth' : 'Please link to an Auth organisation first'">
                        <i class="fas fa-shield-alt"></i>
                     </button>

                     <button type="button" 
                             class="btn-accordion-icon btn-acc-xero" 
                             :class="{ 'is-linked': profileData.hasAuthOrganisation == 1 }" 
                             @click.stop="openInXero"
                             :title="profileData.hasAuthOrganisation == 1 ? 'Open Organisation in Xero' : 'Please link to an Auth organisation first to enable Xero'">
                        <i class="fas fa-file-invoice-dollar"></i>
                     </button>

                     <button type="button" 
                             class="btn-accordion-icon btn-acc-sync" 
                             :class="{ 'is-linked': profileData.hasAuthOrganisation == 1, 'is-syncing': isSyncing }" 
                             @click.stop="manualSyncFromAuth"
                             :disabled="isSyncing || profileData.hasAuthOrganisation != 1"
                             :title="profileData.hasAuthOrganisation == 1 ? (isSyncing ? 'Syncing with Auth...' : 'Sync organisation from Auth') : 'Please link to an Auth organisation first to enable sync'">
                        <i class="fas" :class="isSyncing ? 'fa-spinner fa-spin' : 'fa-sync-alt'"></i>
                     </button>

                     <!-- Expand/Collapse Chevron -->
                     <button type="button" class="btn-accordion-chevron ms-1" @click.stop="toggleSection('organisationDetails')">
                        <i class="fas" :class="sectionOpen.organisationDetails ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                     </button>
                  </div>
               </div>

               <div v-show="sectionOpen.organisationDetails" class="accordion-body p-4 pt-3 border-top">
                  <div class="row g-4">
                     <!-- Left Column -->
                     <div class="col-12 col-lg-6">
                        <div class="mb-3">
                           <label class="form-label">Organisation Name</label>
                           <div class="field-save-wrapper">
                              <input type="text" class="form-control" v-model="profileData.name" @input="debounceSave('Organisation Name', 'name')" :disabled="isLinkedToAuthOrg" />
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.name === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.name === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.name === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                            <small v-if="getDisplayFormerNames()" class="text-muted fst-italic d-block mt-1">
                               <i class="fa-solid fa-history me-1"></i>Formerly: <strong>{{ getDisplayFormerNames() }}</strong>
                            </small>
                        </div>

                        <div class="mb-3">
                           <label class="form-label">Organisation ABN</label>
                           <div class="field-save-wrapper">
                              <input type="text" class="form-control" v-model="profileData.abn" @input="debounceSave('Organisation ABN', 'abn')" :disabled="isLinkedToAuthOrg" />
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.abn === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.abn === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.abn === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>

                        <div class="mb-3">
                           <label class="form-label">Client Type</label>
                           <div class="d-flex align-items-center flex-wrap gap-3">
                              <select class="form-select form-select-sm" style="max-width: 180px;" v-model="primaryClassification" @change="saveClassificationsImmediate" :disabled="isLinkedToAuthOrg || profileData.isAuthSynced || profileData.hasAuthOrganisation === 2">
                                 <option value="Lead">Lead</option>
                                 <option value="Prospect">Prospect</option>
                                 <option value="Client">Client</option>
                              </select>

                              <div class="d-flex align-items-center gap-3">
                                 <label class="checkbox-label mb-0" for="class_Partner" @click.stop>
                                    <input type="checkbox" id="class_Partner" v-model="isPartnerClassification" @change="saveClassificationsImmediate" class="styled-checkbox" />
                                    <span class="checkbox-custom"></span>
                                    <span class="checkbox-text">Partner</span>
                                 </label>
                                 
                                 <label class="checkbox-label mb-0" for="class_Supplier" @click.stop>
                                    <input type="checkbox" id="class_Supplier" v-model="isSupplierClassification" @change="saveClassificationsImmediate" class="styled-checkbox" />
                                    <span class="checkbox-custom"></span>
                                    <span class="checkbox-text">Supplier</span>
                                 </label>

                                 <label class="checkbox-label mb-0" for="profileIsTestData" @click.stop>
                                    <input type="checkbox" id="profileIsTestData" v-model="profileData.isTestData" @change="saveClassificationsImmediate" class="styled-checkbox" />
                                    <span class="checkbox-custom"></span>
                                    <span class="checkbox-text">Test</span>
                                 </label>
                              </div>

                              <div class="field-status-indicator-static ms-1">
                                 <span v-if="fieldStates.classifications === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.classifications === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>
                     </div>

                     <!-- Right Column -->
                     <div class="col-12 col-lg-6">
                        <div class="mb-3">
                           <label class="form-label">Company Background or Purpose</label>
                           <div class="field-save-wrapper">
                              <textarea class="form-control" rows="6" v-model="profileData.background" @input="debounceSave('Company Background', 'background')"></textarea>
                              <div class="field-status-indicator field-status-indicator--textarea">
                                 <span v-if="fieldStates.background === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.background === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.background === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>

            <!-- 2. Contact Information Card -->
            <div class="accordion-card card border-0 shadow-sm mb-3">
               <div class="accordion-header d-flex justify-content-between align-items-center p-3" @click="toggleSection('contactInformation')">
                  <div class="d-flex align-items-center gap-2">
                     <i class="fas fa-address-book text-info"></i>
                     <span class="accordion-title">Contact Information</span>
                  </div>

                  <button type="button" class="btn-accordion-chevron" @click.stop="toggleSection('contactInformation')">
                     <i class="fas" :class="sectionOpen.contactInformation ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                  </button>
               </div>

               <div v-show="sectionOpen.contactInformation" class="accordion-body p-4 pt-3 border-top">
                  <div class="row g-4">
                     <div class="col-12 col-lg-6">
                        <div class="mb-3">
                           <label class="form-label">Organisation Lead Name</label>
                           <div class="field-save-wrapper">
                              <input type="text" class="form-control" v-model="profileData.leadName" @input="debounceSave('Organisation Lead Name', 'leadName')" :disabled="isLinkedToAuthOrg" placeholder="Not Provided" />
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.leadName === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.leadName === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.leadName === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>

                        <div class="mb-3">
                           <label class="form-label">Organisation Lead Email</label>
                           <div class="field-save-wrapper">
                              <input type="email" class="form-control" v-model="profileData.org_lead_email" @input="debounceSave('Organisation Lead Email', 'org_lead_email')" :disabled="isLinkedToAuthOrg" placeholder="Not Provided" />
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.org_lead_email === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.org_lead_email === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.org_lead_email === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>
                     </div>

                     <div class="col-12 col-lg-6">
                        <div class="mb-3">
                           <label class="form-label">Organisation Phone Number</label>
                           <div class="field-save-wrapper">
                              <input type="text" class="form-control" v-model="profileData.phone" @input="debounceSave('Organisation Phone Number', 'phone')" :disabled="isLinkedToAuthOrg" placeholder="Not Provided" />
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.phone === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.phone === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.phone === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>

                     </div>
                  </div>

                  <!-- Organisation Addresses Section -->
                  <div class="mt-4 pt-3 border-top">
                     <div class="d-flex align-items-center gap-2 mb-3">
                        <i class="fas fa-map-marker-alt text-info"></i>
                        <h6 class="mb-0 fw-bold text-dark" style="font-size: 14.5px;">Organisation Addresses</h6>
                     </div>

                     <div class="row g-4">
                        <!-- 1. Organisation Address (Primary) -->
                        <div class="col-12 col-lg-6">
                           <div class="mb-3">
                              <label class="form-label d-flex align-items-center gap-1">
                                 <i class="fas fa-building text-muted"></i>
                                 <span>Organisation Address <small class="text-muted">(Primary)</small></span>
                              </label>
                              <div class="field-save-wrapper">
                                 <input type="text" class="form-control" v-model="combinedAddress" @input="debounceSave('Organisation Address', 'address')" :disabled="isLinkedToAuthOrg" placeholder="Not Provided" />
                                 <div class="field-status-indicator">
                                    <span v-if="fieldStates.address === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.address === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.address === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                              </div>
                           </div>
                        </div>

                        <!-- 2. Delivery Address -->
                        <div class="col-12 col-lg-6">
                           <div class="mb-3">
                              <label class="form-label d-flex align-items-center gap-1">
                                 <i class="fas fa-truck text-muted"></i>
                                 <span>Delivery Address</span>
                              </label>
                              <div class="field-save-wrapper">
                                 <input type="text" class="form-control" v-model="combinedDeliveryAddress" @input="debounceSave('Delivery Address', 'delivery_address')" :disabled="isLinkedToAuthOrg" placeholder="Not Provided" />
                                 <div class="field-status-indicator">
                                    <span v-if="fieldStates.delivery_address === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.delivery_address === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.delivery_address === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                              </div>
                           </div>
                        </div>

                        <!-- 3. Billing Address & Billing Email -->
                        <div class="col-12 col-lg-6">
                           <div class="mb-3">
                              <label class="form-label d-flex align-items-center gap-1">
                                 <i class="fas fa-file-invoice-dollar text-muted"></i>
                                 <span>Billing Address</span>
                              </label>
                              <div class="field-save-wrapper">
                                 <input type="text" class="form-control" v-model="combinedBillingAddress" @input="debounceSave('Billing Address', 'billing_address')" :disabled="isLinkedToAuthOrg" placeholder="Not Provided" />
                                 <div class="field-status-indicator">
                                    <span v-if="fieldStates.billing_address === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.billing_address === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.billing_address === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                              </div>
                           </div>
                           <div class="mb-3">
                              <label class="form-label d-flex align-items-center gap-1">
                                 <i class="fas fa-envelope-open-dollar text-muted"></i>
                                 <span>Billing Email</span>
                              </label>
                              <div class="field-save-wrapper">
                                 <input type="email" class="form-control" v-model="profileData.billing_email" @input="debounceSave('Billing Email', 'billing_email')" :disabled="isLinkedToAuthOrg" placeholder="Not Provided" />
                                 <div class="field-status-indicator">
                                    <span v-if="fieldStates.billing_email === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.billing_email === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.billing_email === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                              </div>
                           </div>
                        </div>

                        <!-- 4. Address for Service of Notices & Service Email -->
                        <div class="col-12 col-lg-6">
                           <div class="mb-3">
                              <label class="form-label d-flex align-items-center gap-1">
                                 <i class="fas fa-envelope-open-text text-muted"></i>
                                 <span>Address for Service of Notices</span>
                              </label>
                              <div class="field-save-wrapper">
                                 <input type="text" class="form-control" v-model="combinedServiceAddress" @input="debounceSave('Address for Service of Notices', 'service_address')" :disabled="isLinkedToAuthOrg" placeholder="Not Provided" />
                                 <div class="field-status-indicator">
                                    <span v-if="fieldStates.service_address === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.service_address === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.service_address === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                              </div>
                           </div>
                           <div class="mb-3">
                              <label class="form-label d-flex align-items-center gap-1">
                                 <i class="fas fa-envelope text-muted"></i>
                                 <span>Service of Notices Email</span>
                              </label>
                              <div class="field-save-wrapper">
                                 <input type="email" class="form-control" v-model="profileData.service_email" @input="debounceSave('Service of Notices Email', 'service_email')" :disabled="isLinkedToAuthOrg" placeholder="Not Provided" />
                                 <div class="field-status-indicator">
                                    <span v-if="fieldStates.service_email === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.service_email === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.service_email === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>

                  <!-- Key Stakeholders & Contacts Preview Section -->
                  <div class="mt-4 pt-3 border-top">
                     <div class="d-flex justify-content-between align-items-center mb-3">
                        <div class="d-flex align-items-center gap-2">
                           <i class="fas fa-users text-info"></i>
                           <h6 class="mb-0 fw-bold text-dark" style="font-size: 14.5px;">Key Stakeholders & Contacts</h6>
                           <span v-if="orgContacts.length" class="badge bg-light text-secondary rounded-pill border px-2 py-0.5" style="font-size: 11px;">
                              {{ orgContacts.length }}
                           </span>
                        </div>
                        <button
                           v-if="orgContacts.length > 0"
                           type="button"
                           class="btn btn-sm btn-link text-info p-0 text-decoration-none fw-semibold d-flex align-items-center gap-1"
                           @click="activeTab = 'contacts'"
                        >
                           <span>View all {{ orgContacts.length }} in Contacts tab</span>
                           <i class="fas fa-arrow-right small"></i>
                        </button>
                     </div>

                     <!-- Mini-grid preview of top contacts -->
                     <div v-if="isContactsLoading" class="text-center py-3 text-muted small">
                        <i class="fas fa-spinner fa-spin me-1 text-info"></i> Loading contacts...
                     </div>
                     <div v-else-if="orgContacts.length === 0" class="p-3 rounded bg-light border text-center text-muted small">
                        <i class="fas fa-user-slash me-1"></i> No contacts found. Link this organisation with Auth or switch to the Contacts tab to sync.
                     </div>
                     <div v-else class="row g-3">
                        <div
                           v-for="contact in previewContacts"
                           :key="contact.id || contact.uuid || contact.email"
                           class="col-12 col-md-6 col-xl-4"
                        >
                           <div class="p-3 rounded bg-light border border-light h-100 contact-preview-mini-card">
                              <div class="d-flex align-items-start justify-content-between gap-2 mb-2">
                                 <div class="d-flex align-items-center gap-2 text-truncate" style="min-width: 0;">
                                    <div class="contact-mini-avatar" :class="'avatar-' + (contact.contact_type || 'default').toLowerCase()">
                                       {{ getContactInitials(contact.first_name, contact.last_name, contact.email) }}
                                    </div>
                                    <div class="text-truncate">
                                       <div class="fw-bold text-dark text-truncate" style="font-size: 13.5px;">
                                          {{ (contact.first_name || '') + ' ' + (contact.last_name || '') }}
                                       </div>
                                       <div class="text-muted small text-truncate" style="font-size: 11.5px;">
                                          {{ contact.role || 'Stakeholder' }}
                                       </div>
                                    </div>
                                 </div>
                                  <div class="d-flex align-items-center flex-shrink-0 ms-2" style="gap: 6px;">
                                     <span
                                        v-if="contact.contact_type"
                                        class="badge rounded-pill text-uppercase px-2 py-0.5 contact-badge-item"
                                        :class="getContactTypeBadgeClass(contact.contact_type)"
                                        style="font-size: 9.5px; font-weight: 700;"
                                     >
                                        {{ contact.contact_type }}
                                     </span>
                                     <span
                                        v-if="contact.is_primary && (!contact.contact_type || contact.contact_type.toLowerCase() !== 'primary')"
                                        class="badge rounded-pill px-1.5 py-0.5 contact-badge-item contact-type-badge-primary"
                                        style="font-size: 9px; font-weight: 600;"
                                     >
                                        ★ Primary
                                     </span>
                                  </div>
                              </div>
                              <div class="small text-muted d-flex flex-column gap-1 pt-2 border-top" style="font-size: 12px;">
                                 <div v-if="contact.email" class="text-truncate d-flex align-items-center justify-content-between">
                                    <a :href="'mailto:' + contact.email" class="text-secondary text-decoration-none text-truncate" :title="contact.email">
                                       <i class="fas fa-envelope text-info me-1"></i>{{ contact.email }}
                                    </a>
                                    <button
                                       type="button"
                                       class="btn btn-sm btn-link text-muted p-0 ms-1 copy-email-btn"
                                       @click.stop="copyContactEmail(contact.email)"
                                       :title="copiedContactEmail === contact.email ? 'Copied!' : 'Copy Email'"
                                    >
                                       <i :class="copiedContactEmail === contact.email ? 'fas fa-check text-success' : 'far fa-copy'"></i>
                                    </button>
                                 </div>
                                 <div v-if="contact.phone_number" class="text-truncate">
                                    <a :href="'tel:' + contact.phone_number" class="text-secondary text-decoration-none">
                                       <i class="fas fa-phone text-info me-1"></i>{{ contact.phone_number }}
                                    </a>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>

            <!-- 3. Relationships Card -->
            <div class="accordion-card card border-0 shadow-sm mb-3">
               <div class="accordion-header d-flex justify-content-between align-items-center p-3" @click="toggleSection('relationships')">
                  <div class="d-flex align-items-center gap-2">
                     <i class="fas fa-network-wired text-info"></i>
                     <span class="accordion-title">Relationships</span>
                  </div>

                  <div class="d-flex align-items-center gap-2" @click.stop>
                     <span v-if="profileData.isMspManaged && profileData.mspUuid" class="rel-header-badge rel-badge-msp">
                        MSP - {{ getMspName(profileData.mspUuid) }}
                     </span>
                     <span v-if="profileData.isPartnerReferred && profileData.partnerUuid" class="rel-header-badge rel-badge-partner">
                        Partner Referred - {{ getPartnerName(profileData.partnerUuid) }}
                     </span>
                     <span v-if="profileData.isEcosystem && profileData.ecosystemUuid" class="rel-header-badge rel-badge-ecosystem">
                        Ecosystem - {{ getEcosystemName(profileData.ecosystemUuid) }}
                     </span>

                     <button type="button" class="btn-accordion-chevron ms-1" @click.stop="toggleSection('relationships')">
                        <i class="fas" :class="sectionOpen.relationships ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                     </button>
                  </div>
               </div>

               <div v-show="sectionOpen.relationships" class="accordion-body p-4 pt-3 border-top">
                  <div class="row g-3">
                     <!-- MSP Managed Section -->
                     <div class="col-12 col-lg-4">
                        <div class="p-3 rounded bg-light border border-light h-100">
                           <div class="d-flex justify-content-between align-items-center mb-2">
                              <span class="form-label mb-0">
                                 <i class="fas fa-network-wired me-2 text-primary"></i>
                                 <strong>MSP Managed</strong>
                              </span>
                              <div class="d-flex align-items-center gap-2">
                                 <div class="field-status-indicator position-static">
                                    <span v-if="fieldStates.isMspManaged === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.isMspManaged === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.isMspManaged === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                                 <label class="switch-toggle mb-0">
                                    <input type="checkbox" v-model="profileData.isMspManaged" @change="debounceSave('MSP Managed Status', 'isMspManaged')" />
                                    <span class="switch-slider"></span>
                                 </label>
                              </div>
                           </div>
                           <div v-if="profileData.isMspManaged" class="ps-2 mt-2 border-start border-primary border-2 animate__animated animate__fadeIn">
                              <label class="form-label text-muted small mb-1">Select MSP</label>
                              <div class="field-save-wrapper">
                                 <select class="form-select form-select-sm" v-model="profileData.mspUuid" @change="debounceSave('MSP Selection', 'mspUuid')">
                                    <option value="">- Select MSP -</option>
                                    <option v-for="opt in mspOptions" :value="opt.id" :key="opt.id">{{ opt.display_name }}</option>
                                 </select>
                                 <div class="field-status-indicator">
                                    <span v-if="fieldStates.mspUuid === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.mspUuid === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.mspUuid === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>

                     <!-- Partner Referred Section -->
                     <div class="col-12 col-lg-4">
                        <div class="p-3 rounded bg-light border border-light h-100">
                           <div class="d-flex justify-content-between align-items-center mb-2">
                              <span class="form-label mb-0">
                                 <i class="fas fa-handshake me-2 text-success"></i>
                                 <strong>Partner Referred</strong>
                              </span>
                              <div class="d-flex align-items-center gap-2">
                                 <div class="field-status-indicator position-static">
                                    <span v-if="fieldStates.isPartnerReferred === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.isPartnerReferred === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.isPartnerReferred === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                                 <label class="switch-toggle mb-0">
                                    <input type="checkbox" v-model="profileData.isPartnerReferred" @change="debounceSave('Partner Referred Status', 'isPartnerReferred')" />
                                    <span class="switch-slider"></span>
                                 </label>
                              </div>
                           </div>
                           <div v-if="profileData.isPartnerReferred" class="ps-2 mt-2 border-start border-success border-2 animate__animated animate__fadeIn">
                              <label class="form-label text-muted small mb-1">Select Partner</label>
                              <div class="field-save-wrapper">
                                 <select class="form-select form-select-sm" v-model="profileData.partnerUuid" @change="debounceSave('Partner Selection', 'partnerUuid')">
                                    <option value="">- Select Partner -</option>
                                    <option v-for="opt in partnerOptions" :value="opt.id" :key="opt.id">{{ opt.display_name }}</option>
                                 </select>
                                 <div class="field-status-indicator">
                                    <span v-if="fieldStates.partnerUuid === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.partnerUuid === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.partnerUuid === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>

                     <!-- Ecosystem Section -->
                     <div class="col-12 col-lg-4">
                        <div class="p-3 rounded bg-light border border-light h-100">
                           <div class="d-flex justify-content-between align-items-center mb-2">
                              <span class="form-label mb-0">
                                 <i class="fas fa-globe me-2 text-info"></i>
                                 <strong>Ecosystem</strong>
                              </span>
                              <div class="d-flex align-items-center gap-2">
                                 <div class="field-status-indicator position-static">
                                    <span v-if="fieldStates.isEcosystem === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.isEcosystem === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.isEcosystem === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                                 <label class="switch-toggle mb-0">
                                    <input type="checkbox" v-model="profileData.isEcosystem" @change="debounceSave('Ecosystem Status', 'isEcosystem')" />
                                    <span class="switch-slider"></span>
                                 </label>
                              </div>
                           </div>
                           <div v-if="profileData.isEcosystem" class="ps-2 mt-2 border-start border-info border-2 animate__animated animate__fadeIn">
                              <label class="form-label text-muted small mb-1">Select Ecosystem</label>
                              <div class="field-save-wrapper">
                                 <select class="form-select form-select-sm" v-model="profileData.ecosystemUuid" @change="debounceSave('Ecosystem Selection', 'ecosystemUuid')">
                                    <option value="">- Select Ecosystem -</option>
                                    <option v-for="opt in ecosystemOptions" :value="opt.id" :key="opt.id">{{ opt.display_name }}</option>
                                 </select>
                                 <div class="field-status-indicator">
                                    <span v-if="fieldStates.ecosystemUuid === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                       <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                    </span>
                                    <span v-else-if="fieldStates.ecosystemUuid === 'saving'" class="status-text saving" title="Saving changes...">
                                       <i class="fas fa-spinner fa-spin text-info"></i>
                                    </span>
                                    <span v-else-if="fieldStates.ecosystemUuid === 'saved'" class="status-text saved" title="Saved!">
                                       <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                    </span>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>

            <!-- 4. Account Information Card -->
            <div class="accordion-card card border-0 shadow-sm mb-3">
               <div class="accordion-header d-flex justify-content-between align-items-center p-3" @click="toggleSection('accountInformation')">
                  <div class="d-flex align-items-center gap-2">
                     <i class="fas fa-id-card text-info"></i>
                     <span class="accordion-title">Account Information</span>
                  </div>

                  <button type="button" class="btn-accordion-chevron" @click.stop="toggleSection('accountInformation')">
                     <i class="fas" :class="sectionOpen.accountInformation ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                  </button>
               </div>

               <div v-show="sectionOpen.accountInformation" class="accordion-body p-4 pt-3 border-top">
                  <div class="row g-4">
                     <div class="col-12 col-lg-6">
                        <div class="mb-3">
                           <label class="form-label">Vertex Lead (Owner)</label>
                           <div class="field-save-wrapper">
                              <select class="form-select" v-model="profileData.assigned_user_id" @change="debounceSave('Vertex Lead', 'assigned_user_id')">
                                 <option :value="0">- No BDM Assigned -</option>
                                 <option v-for="bdm in bdms" :value="bdm.auth_user_id" :key="bdm.db_id">
                                     {{ bdm.name || (bdm.first_name + ' ' + bdm.last_name).trim() || bdm.email }}{{ bdm.email && (bdm.name || bdm.first_name || bdm.last_name) ? ` (${bdm.email})` : '' }}
                                 </option>
                              </select>
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.assigned_user_id === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.assigned_user_id === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.assigned_user_id === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>

                        <div class="mb-3" v-if="primaryClassification === 'Client'">
                           <label class="form-label">Vertex Organisation Lead</label>
                           <div class="field-save-wrapper">
                              <select class="form-select" v-model="profileData.vertex_org_manager_id" @change="debounceSave('Vertex Organisation Lead', 'vertex_org_manager_id')">
                                 <option :value="0">- No Lead Assigned -</option>
                                 <option v-for="user in vertexUsers" :value="user.id" :key="'lead_' + user.id">
                                    {{ user.name || (user.first_name + ' ' + user.last_name).trim() || user.email }}{{ user.email && (user.name || user.first_name || user.last_name) ? ` (${user.email})` : '' }}
                                 </option>
                              </select>
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.vertex_org_manager_id === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.vertex_org_manager_id === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.vertex_org_manager_id === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>

                        <div class="mb-3">
                           <label class="form-label">Organisation Currency</label>
                           <div class="field-save-wrapper">
                              <input type="text" class="form-control" v-model="profileData.currency" @input="debounceSave('Organisation Currency', 'currency')" />
                              <div class="field-status-indicator">
                                 <span v-if="fieldStates.currency === 'waiting'" class="status-text waiting" title="Waiting to auto-save...">
                                    <i class="fas fa-clock text-warning animate__animated animate__pulse animate__infinite"></i>
                                 </span>
                                 <span v-else-if="fieldStates.currency === 'saving'" class="status-text saving" title="Saving changes...">
                                    <i class="fas fa-spinner fa-spin text-info"></i>
                                 </span>
                                 <span v-else-if="fieldStates.currency === 'saved'" class="status-text saved" title="Saved!">
                                    <i class="fas fa-check-circle text-success animate__animated animate__bounceIn"></i>
                                 </span>
                              </div>
                           </div>
                        </div>
                     </div>

                     <div class="col-12 col-lg-6">
                        <!-- Organisation Management -->
                        <div class="mb-3">
                           <label class="form-label">Organisation Management</label>
                           <div class="p-3 bg-light rounded border border-light">
                              <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                                 <div class="d-flex align-items-center gap-2">
                                     <span class="text-muted small fw-semibold">Auth Status:</span>
                                     <span v-if="profileData.isAuthSynced" class="badge bg-success text-white">
                                        <i class="fas fa-sync-alt me-1"></i> Auth Synced
                                     </span>
                                     <span v-else-if="profileData.hasAuthOrganisation == 1" class="badge text-white" style="background-color: #4f46e5;">
                                        <i class="fas fa-link me-1"></i> Manually Linked
                                     </span>
                                     <span v-else-if="profileData.hasAuthOrganisation == 2" class="badge bg-primary text-white">
                                        <i class="fas fa-clock me-1"></i> Marked for Creation
                                     </span>
                                     <span v-else class="badge bg-secondary text-white">
                                        Unlinked
                                     </span>
                                  </div>

                                  <div>
                                     <button v-if="profileData.hasAuthOrganisation == 0 || profileData.hasAuthOrganisation == null"
                                             class="btn btn-sm btn-outline-primary"
                                             title="Mark for Auth Creation"
                                             @click="openMarkAuthModal">
                                        <i class="fas fa-clock me-1"></i> Mark for Creation
                                     </button>
                                     <button v-else-if="profileData.hasAuthOrganisation == 2"
                                             class="btn btn-sm btn-outline-danger"
                                             title="Unmark from Auth Creation"
                                             @click="openUnmarkAuthModal">
                                        <i class="fas fa-times me-1"></i> Unmark from Auth Creation
                                     </button>
                                  </div>
                              </div>

                              <div class="d-flex gap-2">
                                 <button v-if="profileData.isTestData && !isDirty('isTestData')" type="button" class="btn btn-danger btn-sm px-3 d-flex align-items-center gap-2" @click="openDeleteOrgModal" title="Delete Organisation">
                                    <i class="fas fa-trash-alt"></i> Delete Organisation
                                 </button>
                                 <button v-else-if="!profileData.isTestData && !isDirty('isTestData')" type="button" class="btn btn-warning btn-sm px-3 d-flex align-items-center gap-2 text-white" @click="openDeactivateOrgModal" title="Deactivate Organisation" style="background-color: #f59e0b; border-color: #f59e0b;">
                                    <i class="fas fa-ban"></i> Deactivate Organisation
                                 </button>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </div>

         <!-- CONTACTS TAB -->
         <div v-if="activeTab === 'contacts'" class="contacts-tab-content card p-4 border-0 shadow-sm" style="border-radius: 12px;">
            <!-- Header Bar -->
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
               <div>
                  <div class="form-section-title mb-0 border-0 pb-0 d-flex align-items-center gap-2">
                     <i class="fas fa-users text-info"></i>
                     <span>Organisation Contacts & Stakeholders</span>
                     <span v-if="orgContacts.length" class="badge bg-light text-secondary rounded-pill border px-2.5 py-1" style="font-size: 13px;">
                        {{ orgContacts.length }}
                     </span>
                  </div>
                  <p class="text-muted small mb-0 mt-1">
                     Directory of primary leads, technical contacts, billing managers, and synced Auth users.
                  </p>
               </div>

               <div class="d-flex align-items-center gap-2 flex-wrap">
                   <!-- Sync from Auth icon button (matching Organisation Details design) -->
                   <button
                      type="button"
                      class="btn-accordion-icon btn-acc-sync"
                      :class="{ 'is-linked': profileData.hasAuthOrganisation == 1, 'is-syncing': isContactsSyncing }"
                      @click="syncOrganisationContacts"
                      :disabled="isContactsSyncing || profileData.hasAuthOrganisation != 1"
                      :title="profileData.hasAuthOrganisation == 1 ? (isContactsSyncing ? 'Syncing contacts from Auth...' : 'Sync contacts from Auth') : 'Please link to an Auth organisation first to enable sync'"
                      :aria-label="profileData.hasAuthOrganisation == 1 ? (isContactsSyncing ? 'Syncing contacts from Auth...' : 'Sync contacts from Auth') : 'Please link to an Auth organisation first to enable sync'"
                   >
                      <i class="fas" :class="isContactsSyncing ? 'fa-spinner fa-spin' : 'fa-sync-alt'"></i>
                   </button>
               </div>
            </div>

            <!-- Filters & Search Toolbar (Matching All Organisations) -->
            <div class="ms-card mb-4">
               <div class="org-tabs-container">
                  <!-- Role/Type Filter Tabs -->
                  <div class="org-tabs-nav">
                     <button
                        type="button"
                        class="org-tab-btn"
                        :class="{ active: contactsTypeFilter === 'all' }"
                        @click="contactsTypeFilter = 'all'"
                     >
                        <span>All</span>
                        <span class="org-tab-counter-badge">{{ contactTypeCounts.all }}</span>
                     </button>
                     <button
                        v-if="contactTypeCounts.primary"
                        type="button"
                        class="org-tab-btn"
                        :class="{ active: contactsTypeFilter === 'primary' }"
                        @click="contactsTypeFilter = 'primary'"
                     >
                        <i class="fas fa-star text-warning me-1.5" style="font-size: 11px;"></i>
                        <span>Primary</span>
                        <span class="org-tab-counter-badge">{{ contactTypeCounts.primary }}</span>
                     </button>
                     <button
                        v-if="contactTypeCounts.technical"
                        type="button"
                        class="org-tab-btn"
                        :class="{ active: contactsTypeFilter === 'technical' }"
                        @click="contactsTypeFilter = 'technical'"
                     >
                        <span>Technical</span>
                        <span class="org-tab-counter-badge">{{ contactTypeCounts.technical }}</span>
                     </button>
                     <button
                        v-if="contactTypeCounts.billing"
                        type="button"
                        class="org-tab-btn"
                        :class="{ active: contactsTypeFilter === 'billing' }"
                        @click="contactsTypeFilter = 'billing'"
                     >
                        <span>Billing</span>
                        <span class="org-tab-counter-badge">{{ contactTypeCounts.billing }}</span>
                     </button>
                     <button
                        v-if="contactTypeCounts.signer"
                        type="button"
                        class="org-tab-btn"
                        :class="{ active: contactsTypeFilter === 'signer' }"
                        @click="contactsTypeFilter = 'signer'"
                     >
                        <span>Signer</span>
                        <span class="org-tab-counter-badge">{{ contactTypeCounts.signer }}</span>
                     </button>
                     <button
                        v-if="contactTypeCounts.user"
                        type="button"
                        class="org-tab-btn"
                        :class="{ active: contactsTypeFilter === 'user' }"
                        @click="contactsTypeFilter = 'user'"
                     >
                        <span>User</span>
                        <span class="org-tab-counter-badge">{{ contactTypeCounts.user }}</span>
                     </button>
                     <button
                        v-if="contactTypeCounts.lead"
                        type="button"
                        class="org-tab-btn"
                        :class="{ active: contactsTypeFilter === 'lead' }"
                        @click="contactsTypeFilter = 'lead'"
                     >
                        <span>Lead</span>
                        <span class="org-tab-counter-badge">{{ contactTypeCounts.lead }}</span>
                     </button>
                  </div>

                  <!-- Search Input on the right side of the tab bar -->
                  <div class="d-flex align-items-center py-2">
                     <div class="header-search-container m-0">
                        <div class="header-search-input-wrapper">
                           <i class="fas fa-search search-icon"></i>
                           <input
                              type="text"
                              v-model="contactsSearchQuery"
                              class="header-search-input"
                              placeholder="Search contacts by name, role, email..."
                           />
                           <button
                              v-if="contactsSearchQuery"
                              type="button"
                              class="clear-search-btn"
                              @click="contactsSearchQuery = ''"
                              title="Clear search"
                           >
                              <i class="fas fa-times"></i>
                           </button>
                        </div>
                     </div>
                  </div>
               </div>
            </div>

            <!-- Loading State -->
            <div v-if="isContactsLoading" class="py-5 text-center text-muted">
               <i class="fas fa-spinner fa-spin fa-2x text-info mb-2"></i>
               <p class="mb-0 fw-semibold">Loading organisation contacts...</p>
            </div>

            <!-- Empty State -->
            <div v-else-if="filteredOrgContacts.length === 0" class="card border-0 bg-light py-5 text-center text-muted" style="border-radius: 12px;">
               <i class="fas fa-address-book fa-3x mb-3 text-secondary animate__animated animate__pulse animate__infinite" style="opacity: 0.4;"></i>
               <p class="mb-1 fw-bold">
                  {{ contactsSearchQuery || contactsTypeFilter !== 'all' ? 'No contacts match the selected filters.' : 'No contacts found for this organisation.' }}
               </p>
               <p class="small text-muted mb-3">
                  {{ contactsSearchQuery || contactsTypeFilter !== 'all' ? 'Try adjusting your search query or role filter.' : 'Link this organisation to Auth or click Sync to fetch stakeholders.' }}
               </p>
               <div v-if="contactsSearchQuery || contactsTypeFilter !== 'all'">
                  <button type="button" class="btn btn-sm btn-outline-info" @click="contactsSearchQuery = ''; contactsTypeFilter = 'all';">
                     Clear Filters
                  </button>
               </div>
            </div>

            <!-- CARDS GRID VIEW -->
            <div v-else class="row g-4">
               <div
                  v-for="contact in filteredOrgContacts"
                  :key="contact.id || contact.uuid || contact.email"
                  class="col-12 col-md-6 col-xl-4"
               >
                  <div class="card h-100 border-0 shadow-sm contact-directory-card">
                     <div class="card-body p-4 d-flex flex-column h-100">
                        <!-- Top Meta & Avatar -->
                        <div class="d-flex justify-content-between align-items-start mb-3">
                           <div class="d-flex align-items-center gap-3 text-truncate" style="min-width: 0;">
                              <div class="contact-card-avatar" :class="'avatar-' + (contact.contact_type || 'default').toLowerCase()">
                                 {{ getContactInitials(contact.first_name, contact.last_name, contact.email) }}
                              </div>
                              <div class="text-truncate">
                                 <h6 class="mb-0 fw-bold text-dark text-truncate" style="font-size: 15px;">
                                    {{ (contact.first_name || '') + ' ' + (contact.last_name || '') }}
                                 </h6>
                                 <div class="text-muted small text-truncate" style="font-size: 12.5px;">
                                    {{ contact.role || 'Stakeholder' }}
                                 </div>
                              </div>
                           </div>
                           <div class="d-flex align-items-center flex-shrink-0 ms-2" style="gap: 6px;">
                              <span
                                 v-if="contact.contact_type"
                                 class="badge rounded-pill text-uppercase px-2.5 py-1 contact-badge-item"
                                 :class="getContactTypeBadgeClass(contact.contact_type)"
                                 style="font-size: 10px; font-weight: 700; letter-spacing: 0.3px;"
                              >
                                 {{ contact.contact_type }}
                              </span>
                              <span
                                 v-if="contact.is_primary && (!contact.contact_type || contact.contact_type.toLowerCase() !== 'primary')"
                                 class="badge rounded-pill px-2 py-0.5 contact-badge-item contact-type-badge-primary"
                                 style="font-size: 9.5px; font-weight: 600;"
                              >
                                 ★ Primary
                              </span>
                           </div>
                        </div>

                        <!-- Contact Channels -->
                        <div class="contact-card-channels d-flex flex-column gap-2 py-3 border-top border-bottom my-2">
                           <div v-if="contact.email" class="d-flex align-items-center justify-content-between gap-2 text-truncate">
                              <a :href="'mailto:' + contact.email" class="text-secondary text-decoration-none text-truncate d-flex align-items-center gap-2" :title="contact.email" style="font-size: 13px;">
                                 <i class="fas fa-envelope text-info" style="width: 16px;"></i>
                                 <span class="text-truncate">{{ contact.email }}</span>
                              </a>
                              <button
                                 type="button"
                                 class="btn btn-sm btn-link text-muted p-0 flex-shrink-0 copy-email-btn"
                                 @click.stop="copyContactEmail(contact.email)"
                                 :title="copiedContactEmail === contact.email ? 'Copied!' : 'Copy Email'"
                              >
                                 <i :class="copiedContactEmail === contact.email ? 'fas fa-check text-success' : 'far fa-copy'"></i>
                              </button>
                           </div>
                           <div v-if="contact.phone_number" class="d-flex align-items-center gap-2 text-truncate">
                              <a :href="'tel:' + contact.phone_number" class="text-secondary text-decoration-none d-flex align-items-center gap-2" style="font-size: 13px;">
                                 <i class="fas fa-phone text-info" style="width: 16px;"></i>
                                 <span>{{ contact.phone_number }}</span>
                              </a>
                           </div>
                           <div v-if="!contact.email && !contact.phone_number" class="text-muted small fst-italic">
                              No direct channels recorded.
                           </div>
                        </div>

                        <!-- Bottom Actions & Meta -->
                        <div class="mt-auto pt-2 d-flex justify-content-between align-items-center">
                           <span v-if="contact.auth_user_id || contact.auth_contact_id" class="badge bg-light text-secondary border d-inline-flex align-items-center gap-1" style="font-size: 10.5px; font-weight: 500;">
                              <i class="fas fa-shield-alt text-info"></i> Auth Linked
                           </span>
                           <span v-else class="text-muted" style="font-size: 11px;">
                              Spark Contact
                           </span>

                           <button
                              type="button"
                              class="btn btn-sm btn-outline-info d-flex align-items-center gap-1 px-2.5 py-1"
                              style="border-radius: 6px; font-weight: 600; font-size: 12px;"
                              @click="createProposalForContact(contact)"
                              title="Create proposal addressed to this contact"
                           >
                              <i class="fas fa-file-invoice"></i>
                              <span>Create Proposal</span>
                           </button>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </div>

         <!-- JOBS BOARD TAB -->
         <div v-if="activeTab === 'jobs_board'" class="jobs-board-tab-content card p-4 border-0 shadow-sm" style="border-radius: 12px;">
            <div class="d-flex justify-content-between align-items-center mb-4">
               <div class="form-section-title mb-0 border-0 pb-0">Jobs Board</div>
               <router-link to="/jobs" class="btn btn-primary btn-sm d-flex align-items-center gap-1" style="background-color: #00A0C1; border-color: #00A0C1; color: #fff; border-radius: 8px; font-weight: 600; padding: 6px 16px;">
                  <i class="fas fa-briefcase"></i> View All Jobs
               </router-link>
            </div>
            <div class="card border-0 bg-light py-5 text-center text-muted" style="border-radius: 12px;">
               <i class="fas fa-briefcase fa-3x mb-3 text-secondary animate__animated animate__pulse animate__infinite" style="opacity: 0.4;"></i>
               <p class="mb-1 fw-bold">No active jobs found for {{ profileData.name || 'this organisation' }}.</p>
               <p class="small text-muted mb-0">Jobs associated with this organisation will appear here.</p>
            </div>
         </div>

         <!-- CLIENT COMMUNICATION TAB -->
         <div v-if="activeTab === 'client_communication'" class="client-comm-tab-content card p-4 border-0 shadow-sm">
            <div class="d-flex justify-content-between align-items-center mb-4">
               <div class="form-section-title mb-0">Client Communication Preferences</div>
               <button type="button" class="btn btn-primary btn-sm d-flex align-items-center gap-1" style="background-color: #00A0C1; border-color: #00A0C1; color: #fff; border-radius: 8px; font-weight: 600; padding: 6px 16px;" @click="openCreateCommModal">
                  <i class="fas fa-plus"></i> Create Preference
               </button>
            </div>
            
            <div class="row g-4">
               <div class="col-12" v-if="clientCommunications.length === 0">
                  <div class="card border-0 bg-light py-5 text-center text-muted" style="border-radius: 12px;">
                     <i class="fas fa-bullhorn fa-3x mb-3 text-secondary animate__animated animate__pulse animate__infinite" style="opacity: 0.4;"></i>
                     <p class="mb-0 fw-bold">No communication preferences recorded.</p>
                     <p class="text-muted small mt-1">Click "Create Preference" to add communication settings for organisation contacts.</p>
                  </div>
               </div>
               <div v-else class="col-12">
                  <div class="row g-4">
                     <div class="col-12 col-md-6 col-lg-6" v-for="c in clientCommunications" :key="c.id">
                        <div class="card h-100 border-0 shadow-sm comm-preference-card">
                           <div class="card-body p-4 d-flex flex-column h-100">
                              <!-- Card Top: Avatar & Meta & Actions -->
                              <div class="d-flex justify-content-between align-items-start mb-3">
                                 <div class="d-flex align-items-center gap-3">
                                    <div :style="getAvatarStyle(c.is_linked ? (getUserName(c.user_id) || c.name) : c.name)">
                                       {{ getUserInitials(c.is_linked ? (getUserName(c.user_id) || c.name) : c.name) }}
                                    </div>
                                    <div>
                                       <h5 class="card-title fw-bold mb-1 text-dark" style="font-size: 1.05rem;">
                                          {{ c.is_linked ? (getUserName(c.user_id) || c.name) : c.name }}
                                       </h5>
                                       <span v-if="c.is_linked" class="text-muted small">
                                          <i class="far fa-envelope me-1"></i>{{ getUserEmail(c.user_id) }}
                                       </span>
                                       <span v-else class="badge bg-light text-secondary border small py-1 px-2" style="font-weight: 500;">
                                          <i class="fas fa-user-clock me-1"></i>Temporary Contact
                                       </span>
                                    </div>
                                 </div>
                                 <div class="d-flex gap-1 comm-card-actions">
                                    <button class="btn btn-sm btn-icon-only btn-outline-secondary" @click="editCommPrefs(c); showCommModal = true" title="Edit Preference">
                                       <i class="fas fa-edit"></i>
                                    </button>
                                    <button class="btn btn-sm btn-icon-only btn-outline-danger" @click="deleteCommPref(c)" title="Delete Preference">
                                       <i class="fas fa-trash"></i>
                                    </button>
                                 </div>
                              </div>

                              <!-- Card Middle: Badges -->
                              <div class="d-flex flex-wrap gap-2 mb-4 mt-2">
                                 <span :class="['comm-badge', getPaceBadgeClass(c.client_pace)]" :title="getPaceBadgeTooltip(c.client_pace)">
                                    <i class="fas fa-stopwatch"></i>
                                    Pace: {{ getPaceBadgeText(c.client_pace) }}
                                 </span>
                                 <span :class="['comm-badge', getCostBadgeClass(c.cost_sensitivity)]" :title="getCostBadgeTooltip(c.cost_sensitivity)">
                                    <i class="fas fa-dollar-sign"></i>
                                    Cost Sensitivity: {{ getCostBadgeText(c.cost_sensitivity) }}
                                 </span>
                                 <span :class="['comm-badge', getTechBadgeClass(c.technicality)]" :title="getTechBadgeTooltip(c.technicality)">
                                    <i class="fas fa-code"></i>
                                    Tech: {{ getTechBadgeText(c.technicality) }}
                                 </span>
                              </div>

                              <!-- Card Footer: Details (Full Text / same size) -->
                              <div class="mt-auto pt-3 border-top border-light">
                                 <div class="row g-3">
                                    <div class="col-12 col-sm-6">
                                       <div class="fw-bold text-muted small mb-1" style="text-transform: uppercase; letter-spacing: 0.5px; font-size: 0.72rem;">
                                          Decision Style
                                       </div>
                                       <div class="text-secondary small bg-light p-2.5" style="word-break: break-word; min-height: 80px; line-height: 1.4; border-radius: 8px; border: 1.5px solid #f1f5f9; padding: 10px;">
                                          {{ c.decision_style || 'Not specified' }}
                                       </div>
                                    </div>
                                    <div class="col-12 col-sm-6">
                                       <div class="fw-bold text-muted small mb-1" style="text-transform: uppercase; letter-spacing: 0.5px; font-size: 0.72rem;">
                                          Communication Style
                                       </div>
                                       <div class="text-secondary small bg-light p-2.5" style="word-break: break-word; min-height: 80px; line-height: 1.4; border-radius: 8px; border: 1.5px solid #f1f5f9; padding: 10px;">
                                          {{ c.communication_style || 'Not specified' }}
                                       </div>
                                    </div>
                                 </div>
                              </div>

                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </div>

          <!-- MERGED ORGANISATIONS TAB -->
          <div v-if="activeTab === 'merged_organisations'" class="merged-orgs-tab-content card p-4 border-0 shadow-sm">
             <div class="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <div>
                   <h5 class="fw-bold mb-1 text-dark">Merged Organisations</h5>
                   <p class="text-muted small mb-0">Organisations that have been merged into {{ profileData.name }}</p>
                </div>
                <button 
                   v-if="!profileData.is_merged" 
                   type="button" 
                   class="btn btn-sm btn-vertex-primary d-flex align-items-center gap-1"
                   @click="openMergeModal"
                >
                   <i class="fas fa-code-branch"></i> Merge Another Organisation
                </button>
             </div>

             <div v-if="isMergedOrgsLoading" class="text-center py-5">
                <i class="fas fa-spinner fa-spin fa-2x mb-2" style="color: #00768a;"></i>
                <p class="text-muted mb-0">Loading merged organisations...</p>
             </div>

             <div v-else-if="mergedOrganisations && mergedOrganisations.length > 0" class="table-responsive">
                <table class="table table-hover align-middle mb-0">
                   <thead class="table-light">
                      <tr>
                         <th>Organisation</th>
                         <th>Merged Date</th>
                         <th>Merged By</th>
                         <th>Reason</th>
                         <th>Absorbed Assets</th>
                         <th class="text-end">Actions</th>
                      </tr>
                   </thead>
                   <tbody>
                      <tr v-for="mOrg in mergedOrganisations" :key="mOrg.id || mOrg.uuid">
                         <td>
                            <div class="d-flex align-items-center gap-2">
                               <div class="org-avatar-box d-flex align-items-center justify-content-center" style="width: 36px; height: 36px; font-size: 14px; border-radius: 8px; background: rgba(0, 118, 138, 0.08); color: #00768a;">
                                  <i class="fas fa-building"></i>
                               </div>
                               <div>
                                  <a href="javascript:void(0)" @click="navigateToOrganisation(mOrg.uuid || mOrg.id)" class="fw-bold text-dark text-decoration-none" style="cursor: pointer;" onmouseover="this.style.color='#00768a'; this.style.textDecoration='underline';" onmouseout="this.style.color='#1e293b'; this.style.textDecoration='none';">{{ mOrg.name }}</a>
                                  <div class="text-muted small">{{ mOrg.abn || mOrg.email || 'No ABN' }}</div>
                                </div>
                            </div>
                         </td>
                         <td>
                            <span class="text-muted small">{{ mOrg.merged_at ? formatDate(mOrg.merged_at) : 'N/A' }}</span>
                         </td>
                         <td>
                            <span class="badge bg-light text-secondary border">{{ mOrg.merged_by_name || 'System' }}</span>
                         </td>
                         <td>
                            <span class="text-muted small fst-italic">{{ mOrg.merge_reason || 'No reason specified' }}</span>
                         </td>
                         <td>
                            <div class="d-flex align-items-center gap-2 small">
                               <span class="badge" style="background-color: rgba(0, 118, 138, 0.1); color: #00768a; border: 1px solid rgba(0, 118, 138, 0.2); padding: 4px 8px;">
                                  <i class="fas fa-bullseye me-1"></i>{{ mOrg.transferred_leads_count || 0 }} Leads
                                </span>
                               <span class="badge" style="background-color: rgba(0, 160, 193, 0.1); color: #00A0C1; border: 1px solid rgba(0, 160, 193, 0.2); padding: 4px 8px;">
                                  <i class="fas fa-users me-1"></i>{{ mOrg.transferred_contacts_count || 0 }} Contacts
                               </span>
                            </div>
                         </td>
                         <td class="text-end">
                            <div class="d-flex justify-content-end align-items-center gap-2">
                               <button type="button" @click="navigateToOrganisation(mOrg.uuid || mOrg.id)" class="btn btn-sm btn-outline-secondary" title="View Organisation">
                                  <i class="fas fa-external-link-alt me-1"></i> View
                               </button>
                               <button type="button" class="btn btn-sm btn-vertex-outline" @click="openUnmergeModal(mOrg)" title="Unmerge Organisation">
                                  <i class="fas fa-undo me-1"></i> Unmerge
                               </button>
                            </div>
                         </td>
                      </tr>
                   </tbody>
                </table>
             </div>

             <div v-else class="text-center py-5 text-muted">
                <div class="mb-3">
                   <i class="fas fa-code-branch fa-3x text-muted" style="opacity: 0.35;"></i>
                </div>
                <h6 class="fw-bold text-secondary mb-1">No Merged Organisations</h6>
                <p class="small text-muted mb-3">No other organisations have been merged into this organisation yet.</p>
                <button v-if="!profileData.is_merged" type="button" class="btn btn-sm btn-vertex-primary" @click="openMergeModal">
                   <i class="fas fa-code-branch me-1"></i> Merge an Organisation to Another
                </button>
             </div>
          </div>

         <!-- TIMELINE TAB -->
         <div v-if="activeTab === 'timeline'" class="timeline-tab-content card p-4 border-0 shadow-sm">
            <div class="form-section-title mb-4">Activity Timeline</div>
            <div v-if="timelineEvents && timelineEvents.length > 0" class="vertical-timeline">
               <div class="timeline-event" v-for="event in timelineEvents" :key="event.id">
                  <div class="timeline-icon">
                     <img v-if="event.image" :src="event.image" style="width: 100%; height: 100%; object-fit: contain; border-radius: 50%; padding: 2px;" />
                     <i v-else :class="event.icon"></i>
                  </div>
                  <div class="timeline-body">
                     <div class="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-2">
                        <div class="d-flex align-items-center gap-2 flex-wrap">
                           <span class="event-title fw-bold text-dark">{{ event.title }}</span>
                           <span v-if="event.isSystem" class="badge bg-light text-secondary border small px-2 py-0.5 d-inline-flex align-items-center" style="font-size: 0.75rem; font-weight: 500;">
                              <i class="fas fa-robot me-1 text-muted"></i>System Generated
                           </span>
                           <span v-else class="badge bg-light text-primary border small px-2 py-0.5 d-inline-flex align-items-center" style="font-size: 0.75rem; font-weight: 500;">
                              <i class="fas fa-user-circle me-1 text-primary"></i>{{ event.userName }}
                           </span>
                        </div>
                        <span class="event-time text-muted small">{{ event.time }}</span>
                     </div>
                     <p class="event-description mb-0 text-muted">{{ event.description }}</p>
                  </div>
               </div>
            </div>
            <div v-else class="text-center py-5 text-muted">
               <i class="fas fa-history fa-2x mb-2 text-muted" style="opacity: 0.5;"></i>
               <p class="mb-0">No timeline events recorded yet.</p>
            </div>
         </div>

         <!-- ── Client Communication Modal ── -->
         <Transition name="lead-modal-fade">
            <div v-if="showCommModal" class="modal-backdrop-custom" @click.self="closeCommModal">
               <div class="modal-content-custom" style="max-width: 550px;">
                  <button type="button" @click="closeCommModal" class="close-button-custom">&times;</button>
                  <h5 class="modal-title-custom">{{ editingCommId ? 'Edit' : 'Add' }} Client Preference</h5>
                  <hr>
                  <div class="modal-body-custom" style="max-height: 65vh; overflow-y: auto; padding-right: 4px;">
                     <div class="mb-3">
                        <template v-if="profileData.hasAuthOrganisation == 1">
                           <label class="form-label fw-bold">Select Organisation User(s) <span class="text-danger">*</span></label>
                           <VueMultiselect
                              v-model="selectedUsers"
                              :options="filteredOrgUsers"
                              :multiple="true"
                              label="name"
                              track-by="id"
                              placeholder="Select User(s)..."
                              :show-labels="false"
                              :close-on-select="false"
                              :disabled="!!editingCommId"
                           />
                        </template>
                        <template v-else>
                           <label class="form-label fw-bold">Contact Name <span class="text-danger">*</span></label>
                           <input type="text" class="form-control" v-model="commForm.name" placeholder="John Doe" :disabled="!!editingCommId" />
                        </template>
                     </div>
                     
                     <div class="mb-3">
                        <label class="form-label fw-bold">Client Pace</label>
                        <select class="form-select" v-model="commForm.clientPace">
                           <option value="" disabled>- Select Client Pace -</option>
                           <option value="Methodical / Relaxed">Methodical / Relaxed</option>
                           <option value="Moderate / Standard">Moderate / Standard</option>
                           <option value="Fast-Paced">Fast-Paced</option>
                           <option value="Urgent / Reactive">Urgent / Reactive</option>
                        </select>
                        <div v-if="commForm.clientPace" class="form-text mt-2 text-muted small bg-light p-2 rounded border-start border-warning" style="border-left-width: 4px !important; line-height: 1.4;">
                           <i class="fas fa-info-circle text-warning me-1"></i>
                           <strong>Pace Guideline:</strong> {{ getPaceExplainer(commForm.clientPace) }}
                        </div>
                     </div>

                     <div class="mb-3">
                        <label class="form-label fw-bold">Cost Sensitivity</label>
                        <select class="form-select" v-model="commForm.costSensitivity">
                           <option value="" disabled>- Select Cost Sensitivity -</option>
                           <option value="High (Highly Budget-Conscious)">High (Highly Budget-Conscious)</option>
                           <option value="Moderate (Value-Driven)">Moderate (Value-Driven)</option>
                           <option value="Low (Not a Primary Concern)">Low (Not a Primary Concern)</option>
                        </select>
                        <div v-if="commForm.costSensitivity" class="form-text mt-2 text-muted small bg-light p-2 rounded border-start border-primary" style="border-left-width: 4px !important; line-height: 1.4;">
                           <i class="fas fa-info-circle text-primary me-1"></i>
                           <strong>Budget Guideline:</strong> {{ getCostExplainer(commForm.costSensitivity) }}
                        </div>
                     </div>

                     <div class="mb-3">
                        <label class="form-label fw-bold">Technicality</label>
                        <select class="form-select" v-model="commForm.technicality">
                           <option value="" disabled>- Select Technicality -</option>
                           <option value="Low (Business-Focused)">Low (Business-Focused)</option>
                           <option value="Moderate (Tech-Savvy)">Moderate (Tech-Savvy)</option>
                           <option value="High (Expert / Developer Level)">High (Expert / Developer Level)</option>
                        </select>
                        <div v-if="commForm.technicality" class="form-text mt-2 text-muted small bg-light p-2 rounded border-start" style="border-left-width: 4px !important; line-height: 1.4; border-color: #7c3aed !important;">
                           <i class="fas fa-info-circle me-1" style="color: #7c3aed;"></i>
                           <strong>Technical Guideline:</strong> {{ getTechExplainer(commForm.technicality) }}
                        </div>
                     </div>

                     <div class="mb-3">
                        <label class="form-label fw-bold">Decision Style</label>
                        <textarea class="form-control" rows="3" v-model="commForm.decisionStyle" placeholder="eg. Mostly prefers options but sometimes needs to be provided strong recommendations"></textarea>
                     </div>

                     <div class="mb-3">
                        <label class="form-label fw-bold">Communication Style</label>
                        <textarea class="form-control" rows="3" v-model="commForm.communicationStyle" placeholder="eg. Likes to discuss security concepts, talk tech and come to an agreement through discussions"></textarea>
                     </div>

                     <div class="mt-4 d-flex justify-content-end gap-2 pt-3 border-top">
                        <button type="button" class="btn btn-secondary" @click="closeCommModal" style="border-radius: 8px; font-weight: bold; padding: 10px 24px;">Cancel</button>
                        <button type="button" class="btn btn-primary add-button-custom" style="background-color: #00A0C1; border-color: #00A0C1; color: #fff; padding: 10px 28px;" @click="saveCommPrefs" :disabled="isLoadingUsers || isLoading || (!editingCommId && ((profileData.hasAuthOrganisation == 1 && selectedUsers.length === 0) || (profileData.hasAuthOrganisation != 1 && !commForm.name.trim())))">
                           <i class="fas fa-save me-1"></i> Save Preference
                        </button>
                     </div>
                  </div>
               </div>
            </div>
         </Transition>


      <!-- ── Delete Organisation Confirmation Modal ── -->
      <Transition name="lead-modal-fade">
         <div v-if="showDeleteOrgModal" class="lead-modal-overlay" @click.self="closeDeleteOrgModal">
            <div class="lead-modal" style="max-width: 450px;">
               <div class="lead-modal__header">
                  <h5 class="lead-modal__title"><i class="fas fa-exclamation-triangle text-danger"></i> Delete Organisation</h5>
                  <button type="button" @click="closeDeleteOrgModal" class="lead-modal__close">&times;</button>
               </div>
               <div class="lead-modal__divider"></div>
               <div class="lead-modal__body p-4 text-center">
                  <div class="mb-3">
                     <i class="fas fa-trash-alt fa-3x text-danger animate__animated animate__bounceIn"></i>
                  </div>
                  <h6 class="fw-bold text-dark mb-3">Confirm Permanent Deletion</h6>
                  <p class="text-muted mb-4">
                     Are you sure you want to delete this organisation and relevant data?
                  </p>
                  <div class="alert alert-danger text-start small">
                     <i class="fas fa-exclamation-circle me-1"></i>
                     This action is permanent and will cascade to all associated Leads, Notes, Proposals, and Contracts.
                  </div>
                  <div class="lead-modal__actions d-flex justify-content-center gap-2 mt-4">
                     <button type="button" class="btn btn-secondary px-4" @click="closeDeleteOrgModal">Cancel</button>
                     <button type="button" class="btn btn-danger px-4" @click="deleteOrganisation" :disabled="isDeleting">
                        <span v-if="isDeleting"><i class="fas fa-spinner fa-spin me-1"></i> Deleting...</span>
                        <span v-else><i class="fas fa-trash-alt me-1"></i> Yes, Delete</span>
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </Transition>

      <!-- ── Deactivate Organisation Confirmation Modal ── -->
      <Transition name="lead-modal-fade">
         <div v-if="showDeactivateOrgModal" class="lead-modal-overlay" @click.self="closeDeactivateOrgModal">
            <div class="lead-modal" style="max-width: 450px;">
               <div class="lead-modal__header">
                  <h5 class="lead-modal__title"><i class="fas fa-exclamation-triangle text-warning"></i> Deactivate Organisation</h5>
                  <button type="button" @click="closeDeactivateOrgModal" class="lead-modal__close">&times;</button>
               </div>
               <div class="lead-modal__divider"></div>
               <div class="lead-modal__body p-4 text-center">
                  <div class="mb-3">
                     <i class="fas fa-ban fa-3x text-warning animate__animated animate__bounceIn"></i>
                  </div>
                  <h6 class="fw-bold text-dark mb-3">Confirm Deactivation</h6>
                  <p class="text-muted mb-4">
                     Are you sure you want to deactivate this organisation?
                  </p>
                  <div class="alert alert-warning text-start small" style="background-color: #fffbeb; border-color: #fde68a; color: #92400e;">
                     <i class="fas fa-info-circle me-1"></i>
                     This will soft-delete the organisation record. Associated leads, proposals, and contracts will remain intact in the database but the organisation will be set as inactive.
                  </div>
                  <div class="lead-modal__actions d-flex justify-content-center gap-2 mt-4">
                     <button type="button" class="btn btn-secondary px-4" @click="closeDeactivateOrgModal">Cancel</button>
                     <button type="button" class="btn btn-warning px-4 text-white" @click="deactivateOrganisation" :disabled="isDeactivating" style="background-color: #f59e0b; border-color: #f59e0b;">
                        <span v-if="isDeactivating"><i class="fas fa-spinner fa-spin me-1"></i> Deactivating...</span>
                        <span v-else><i class="fas fa-ban me-1"></i> Yes, Deactivate</span>
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </Transition>

      <!-- ── Merge Organisation Modal ── -->
      <Transition name="lead-modal-fade">
         <div v-if="showMergeModal" class="lead-modal-overlay" @click.self="closeMergeModal">
            <div class="lead-modal" style="max-width: 680px;">
               <div class="lead-modal__header">
                  <h5 class="lead-modal__title">
                     <i class="fas fa-code-branch" style="color: #00768a;"></i> Merge Organisation
                  </h5>
                  <button type="button" @click="closeMergeModal" class="lead-modal__close">&times;</button>
               </div>
               <div class="lead-modal__divider"></div>
               <div class="lead-modal__body p-4">
                  <div class="alert d-flex align-items-start gap-2 small mb-3" style="background-color: #f0fdfa; border: 1px solid #99f6e4; border-left: 4px solid #00768a; color: #134e4a;">
                     <i class="fas fa-info-circle mt-1" style="color: #00768a;"></i>
                     <div>
                         Merging <strong>{{ profileData.name }}</strong> will transfer all associated leads, contacts, and communication preferences into the selected destination organisation.
                     </div>
                  </div>

                  <div v-if="profileData.has_auth_organisation == 1 || profileData.is_auth_synced || (profileData.auth_organisation_id && profileData.auth_organisation_id > 0)" class="alert d-flex align-items-center gap-2 small py-2 px-3 mb-3 border-0 rounded-3" style="background-color: #eff6ff; color: #1e40af; border-left: 4px solid #3b82f6 !important;">
                     <i class="fas fa-link text-primary"></i>
                     <div>
                        <strong>Auth-Synced Organisation:</strong> Because this organisation is linked/synced with Auth, only other <strong>Auth-synced organisations</strong> can be selected as the destination.
                     </div>
                  </div>

                  <!-- Destination Search / Autocomplete -->
                  <div class="mb-3">
                     <label class="lead-form-label fw-bold">Destination Organisation <span class="text-danger">*</span></label>
                     <VueMultiselect
                        v-model="selectedMergeTarget"
                        :options="mergeSearchResults"
                        :searchable="true"
                        :loading="isSearchingMergeTargets"
                        :internal-search="true"
                        :clear-on-select="false"
                        :close-on-select="true"
                        :preserve-search="false"
                        placeholder="Search destination organisation..."
                        label="name"
                        track-by="id"
                        :show-no-results="true"
                        select-label=""
                        deselect-label=""
                        selected-label=""
                        class="merge-target-multiselect"
                     >
                        <template #option="props">
                           <div class="merge-multiselect-option d-flex flex-column py-1">
                              <div class="d-flex align-items-center gap-2">
                                 <span class="merge-option-name fw-bold">{{ props.option.name }}</span>
                                 <span v-if="props.option.has_auth_organisation == 1 || props.option.is_auth_synced || (props.option.auth_organisation_id && props.option.auth_organisation_id > 0)" class="merge-auth-badge badge bg-primary-subtle text-primary border border-primary-subtle flex-shrink-0" style="font-size: 0.65rem; padding: 2px 6px;">
                                    <i class="fas fa-link me-1"></i>Auth Synced
                                 </span>
                              </div>
                              <span class="merge-option-sub small">{{ props.option.abn || props.option.email || 'No ABN' }}</span>
                           </div>
                        </template>
                        <template #noResult>
                           <div class="text-muted small py-1">No matching organisations found</div>
                        </template>
                     </VueMultiselect>
                     <div class="form-text text-muted small mt-1" v-if="mergeSearchResults && mergeSearchResults.length > 0">
                        {{ mergeSearchResults.length }} organisation{{ mergeSearchResults.length === 1 ? '' : 's' }} available to merge into
                     </div>
                  </div>

                  <!-- Merge Reason -->
                  <div class="mb-3">
                     <label class="lead-form-label fw-bold">Reason for Merge (Optional)</label>
                     <textarea 
                        v-model="mergeReason" 
                        class="form-control" 
                        rows="2" 
                        placeholder="e.g. Company acquisition, duplicate record, restructuring..."
                     ></textarea>
                  </div>

                  <!-- Confirmation Checkbox -->
                  <div 
                     class="vertex-checkbox-card d-flex align-items-start gap-2 p-3 rounded-3 mb-3"
                     :class="{ 'vertex-checkbox-card--checked': mergeConfirmationChecked }"
                     @click="mergeConfirmationChecked = !mergeConfirmationChecked"
                  >
                     <div class="vertex-custom-checkbox pt-1" @click.stop>
                        <input 
                           class="form-check-input vertex-checkbox-input" 
                           type="checkbox" 
                           id="mergeConfirmCheckbox" 
                           v-model="mergeConfirmationChecked"
                        />
                     </div>
                     <label class="form-check-label small text-dark mb-0 user-select-none" for="mergeConfirmCheckbox" style="cursor: pointer; line-height: 1.45;" @click.stop="mergeConfirmationChecked = !mergeConfirmationChecked">
                        I understand that all active records from <strong>{{ profileData.name }}</strong> will be reassigned to <strong>{{ selectedMergeTarget ? selectedMergeTarget.name : 'the destination organisation' }}</strong>.
                     </label>
                  </div>

                  <div class="lead-modal__actions d-flex justify-content-end gap-2 mt-4">
                     <button type="button" class="btn btn-secondary px-3" @click="closeMergeModal">Cancel</button>
                     <button 
                        type="button" 
                        class="btn btn-vertex-primary px-4" 
                        :disabled="!selectedMergeTarget || !mergeConfirmationChecked || isMerging" 
                        @click="confirmMerge"
                     >
                        <i v-if="isMerging" class="fas fa-spinner fa-spin me-1"></i>
                        <i v-else class="fas fa-code-branch me-1"></i>
                        {{ isMerging ? 'Merging...' : 'Confirm Merge' }}
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </Transition>

      <!-- ── Unmerge Organisation Modal ── -->
      <Transition name="lead-modal-fade">
         <div v-if="showUnmergeModal" class="lead-modal-overlay" @click.self="closeUnmergeModal">
            <div class="lead-modal" style="max-width: 480px;">
               <div class="lead-modal__header">
                  <h5 class="lead-modal__title">
                     <i class="fas fa-undo" style="color: #00768a;"></i> Unmerge Organisation
                  </h5>
                  <button type="button" @click="closeUnmergeModal" class="lead-modal__close">&times;</button>
               </div>
               <div class="lead-modal__divider"></div>
               <div class="lead-modal__body p-4">
                  <div class="text-center mb-3">
                     <div class="d-inline-flex align-items-center justify-content-center rounded-circle mb-2" style="width: 56px; height: 56px; background-color: rgba(0, 118, 138, 0.1); color: #00768a;">
                        <i class="fas fa-undo fa-2x"></i>
                     </div>
                     <h6 class="fw-bold text-dark">Confirm Unmerge</h6>
                  </div>
                  <p class="small text-muted mb-3">
                     Are you sure you want to unmerge <strong>{{ unmergeTargetOrg ? (unmergeTargetOrg.name || unmergeTargetOrg.organisation_name) : '' }}</strong>? This will restore the organisation back to active status and return all originally transferred leads, contacts, and preferences.
                  </p>
                  <div class="lead-modal__actions d-flex justify-content-end gap-2 mt-4">
                     <button type="button" class="btn btn-secondary px-3" @click="closeUnmergeModal">Cancel</button>
                     <button 
                        type="button" 
                        class="btn btn-vertex-primary px-4" 
                        :disabled="isUnmerging" 
                        @click="confirmUnmerge"
                     >
                        <i v-if="isUnmerging" class="fas fa-spinner fa-spin me-1"></i>
                        <i v-else class="fas fa-undo me-1"></i>
                        {{ isUnmerging ? 'Unmerging...' : 'Confirm Unmerge' }}
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </Transition>

      <!-- ── Mark / Unmark Auth Creation Modal ── -->
      <Transition name="lead-modal-fade">
         <div v-if="authMarkModal.show" class="lead-modal-overlay" @click.self="closeAuthMarkModal">
            <div class="lead-modal" style="max-width: 460px;">
               <div class="lead-modal__header">
                  <h5 class="lead-modal__title">
                     <i :class="authMarkModal.type === 'mark' ? 'fas fa-clock text-info' : 'fas fa-times-circle text-danger'"></i>
                     {{ authMarkModal.type === 'mark' ? 'Mark for Auth Creation' : 'Unmark from Auth Creation' }}
                  </h5>
                  <button type="button" @click="closeAuthMarkModal" class="lead-modal__close">&times;</button>
               </div>
               <div class="lead-modal__divider"></div>
               <div class="lead-modal__body p-4 text-center">
                  <div class="mb-3">
                     <i v-if="authMarkModal.type === 'mark'" class="fas fa-clock fa-3x animate__animated animate__bounceIn" style="color: #00A0C1;"></i>
                     <i v-else class="fas fa-times-circle fa-3x text-danger animate__animated animate__bounceIn"></i>
                  </div>
                  <h6 class="fw-bold text-dark mb-2">
                     {{ authMarkModal.type === 'mark' ? 'Confirm Auth Creation Queue' : 'Confirm Unmark from Auth' }}
                  </h6>
                  <p class="text-muted mb-3" style="font-size: 0.95rem;">
                     Are you sure you want to {{ authMarkModal.type === 'mark' ? 'mark' : 'unmark' }}
                     <strong class="text-dark">{{ profileData.name || 'this organisation' }}</strong>
                     {{ authMarkModal.type === 'mark' ? 'for creation in Auth?' : 'from Auth creation?' }}
                  </p>
                  <div v-if="authMarkModal.type === 'mark'" class="alert alert-info text-start small mb-0" style="background-color: #f0f9ff; border-color: #bae6fd; color: #0369a1;">
                     <i class="fas fa-info-circle me-1"></i>
                     This organisation will be queued for creation in Auth.
                  </div>
                  <div v-else class="alert alert-warning text-start small mb-0" style="background-color: #fffbeb; border-color: #fde68a; color: #92400e;">
                     <i class="fas fa-exclamation-triangle me-1"></i>
                     This organisation will be removed from the Auth creation queue and reverted to unlinked.
                  </div>
                  <div class="lead-modal__actions d-flex justify-content-center gap-2 mt-4">
                     <button type="button" class="btn btn-secondary px-4" @click="closeAuthMarkModal" :disabled="authMarkModal.isSubmitting">Cancel</button>
                     <button type="button" 
                             class="btn px-4 text-white" 
                             :class="authMarkModal.type === 'mark' ? 'btn-vertex-primary' : 'btn-danger'" 
                             @click="confirmAuthMarkAction" 
                             :disabled="authMarkModal.isSubmitting">
                        <span v-if="authMarkModal.isSubmitting">
                           <i class="fas fa-spinner fa-spin me-1"></i> Processing...
                        </span>
                        <span v-else>
                           <i :class="authMarkModal.type === 'mark' ? 'fas fa-check me-1' : 'fas fa-times me-1'"></i>
                           {{ authMarkModal.type === 'mark' ? 'Yes, Mark Organisation' : 'Yes, Unmark' }}
                        </span>
                     </button>
                  </div>
               </div>
            </div>
         </div>
      </Transition>
   </div>
</div>
</template>

<script>
import moment from 'moment';
import LoadingPage from '@/components/Utilities/vertex-loading.vue';
import VueMultiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import "@/assets/scss/vue-multiselect.scss";

export default {
   name: 'ClientsDashboard',
   components: {
      LoadingPage,
      VueMultiselect
   },
   data() {
      return {
         isLoading: false,
         isSyncing: false,
         activeTab: 'profile',
         sectionOpen: {
            organisationDetails: true,
            contactInformation: false,
            relationships: false,
            accountInformation: false
         },
         selectedLeadId: '',
         leads: [],
         searchQuery: '',
         showSearchDropdown: false,
         selectedPartnerToAdd: '',
         availablePartners: ['Cyber Alliance', 'SecureNet Inc', 'Defendify Ltd', 'ShieldCorp'],
          mspOptions: [],
          partnerOptions: [],
          ecosystemOptions: [],
          showCommModal: false,
          showDeleteOrgModal: false,
         isDeleting: false,
         showDeactivateOrgModal: false,
         isDeactivating: false,
         mergedOrganisations: [],
         isMergedOrgsLoading: false,
         showMergeModal: false,
         isMerging: false,
         selectedMergeTarget: null,
         mergeSearchResults: [],
         isSearchingMergeTargets: false,
         mergeReason: '',
         mergeConfirmationChecked: false,
         showUnmergeModal: false,
         isUnmerging: false,
         unmergeTargetOrg: null,
         bdms: [],
         vertexUsers: [],
         authMarkModal: {
            show: false,
            type: 'mark',
            isSubmitting: false
         },
         profileData: {
            id: '',
            uuid: '',
            name: '',
            abn: '',
            firstName: '',
            lastName: '',
            leadName: '',
            phone: '',
            email: '',
            address: '',
            background: '',
            firstContactDate: '',
            clientType: 'Lead',
            isTestData: false,
            isAuthSynced: false,
            isSupplier: false,
            isPartner: true,
            currency: 'AUD',
            assigned_user_id: 0,
            clients: [],
            suppliers: [],
            partners: [],
            leadTypeId: '',
            leadStatusId: '',
            leadPriorityId: '',
            hasAuthOrganisation: 0,
            authOrganisationName: '',
            authOrganisationUuid: '',
            authOrganisationId: 0,
            organisationUuid: '',
            xeroContactId: '',
            isMspManaged: false,
            mspUuid: '',
            isPartnerReferred: false,
            partnerUuid: '',
            isEcosystem: false,
            ecosystemUuid: '',
            is_merged: false,
            merged_into_uuid: '',
            merged_into_name: '',
            merged_at: null,
            merged_by: 0,
            merged_by_name: '',
            merge_reason: '',
            merge_snapshot: '',
            former_names: []
         },
          timelineEvents: [],
          initialProfileData: {},
          orgContacts: [],
          isContactsLoading: false,
          isContactsSyncing: false,
          contactsSearchQuery: '',
          contactsTypeFilter: 'all',
          copiedContactEmail: null,
          clientCommunications: [],
          orgUsers: [],
         selectedUsers: [],
         commForm: {
            name: '',
            clientPace: '',
            costSensitivity: '',
            technicality: '',
            decisionStyle: '',
            communicationStyle: ''
         },
         editingCommId: null,
         isLoadingUsers: false,
         fieldStates: {
            name: 'idle',
            abn: 'idle',
            leadName: 'idle',
            phone: 'idle',
            email: 'idle',
            org_lead_email: 'idle',
            address: 'idle',
            address2: 'idle',
            city: 'idle',
            state: 'idle',
            postcode: 'idle',
            country: 'idle',
            billing_address: 'idle',
            billing_address2: 'idle',
            billing_city: 'idle',
            billing_state: 'idle',
            billing_postcode: 'idle',
            billing_country: 'idle',
            billing_email: 'idle',
            delivery_address: 'idle',
            delivery_address2: 'idle',
            delivery_city: 'idle',
            delivery_state: 'idle',
            delivery_postcode: 'idle',
            delivery_country: 'idle',
            service_address: 'idle',
            service_address2: 'idle',
            service_city: 'idle',
            service_state: 'idle',
            service_postcode: 'idle',
            service_country: 'idle',
            service_email: 'idle',
            background: 'idle',
            currency: 'idle',
            assigned_user_id: 'idle',
            vertex_org_manager_id: 'idle',
            classifications: 'idle',
            isMspManaged: 'idle',
            mspUuid: 'idle',
            isPartnerReferred: 'idle',
            partnerUuid: 'idle',
            isEcosystem: 'idle',
            ecosystemUuid: 'idle'
         }
      };
   },
   created() {
      this.fetchLeads();
      this.getBdms();
      this.getVertexUsers();
      this.fetchDropdownCategories();
      this.debounceTimeouts = {};
   },
   mounted() {
      document.addEventListener('click', this.handleDocumentClick);
   },
   beforeUnmount() {
      document.removeEventListener('click', this.handleDocumentClick);
      if (this.debounceTimeouts) {
         for (const key in this.debounceTimeouts) {
            clearTimeout(this.debounceTimeouts[key]);
         }
      }
   },
   computed: {
      searchDropdownList() {
         if (!this.searchQuery) return [];
         const query = this.searchQuery.toLowerCase();
         return this.leads.filter(c => 
            (c.organisation_name && c.organisation_name.toLowerCase().includes(query)) ||
            (c.email && c.email.toLowerCase().includes(query)) ||
            (c.former_names && c.former_names.some(fn => fn.toLowerCase().includes(query)))
         );
      },
      isLinkedToAuthOrg() {
         return !!this.profileData.isAuthSynced;
      },
      selectedOrgObject: {
         get() {
            return this.leads.find(l => l.id === this.selectedLeadId) || null;
         },
         set(val) {
            if (val) {
               this.selectedLeadId = val.id;
            } else {
               this.selectedLeadId = '';
            }
         }
      },
      primaryClassification: {
         get() {
            if (!this.profileData || !this.profileData.classifications) return 'Lead';
            if (this.profileData.classifications.includes('Client')) return 'Client';
            if (this.profileData.classifications.includes('Prospect')) return 'Prospect';
            return 'Lead';
         },
         set(val) {
            if (!this.profileData) return;
            let list = (this.profileData.classifications || []).filter(c => c !== 'Lead' && c !== 'Prospect' && c !== 'Client');
            list.push(val);
            this.profileData.classifications = list;
         }
      },
      isPartnerClassification: {
         get() {
            return this.profileData && this.profileData.classifications && this.profileData.classifications.includes('Partner');
         },
         set(checked) {
            if (!this.profileData) return;
            let list = [...(this.profileData.classifications || [])];
            if (checked) {
               if (!list.includes('Partner')) {
                  list.push('Partner');
               }
            } else {
               list = list.filter(c => c !== 'Partner');
            }
            this.profileData.classifications = list;
         }
      },
      isSupplierClassification: {
         get() {
            return this.profileData && this.profileData.classifications && this.profileData.classifications.includes('Supplier');
         },
         set(checked) {
            if (!this.profileData) return;
            let list = [...(this.profileData.classifications || [])];
            if (checked) {
               if (!list.includes('Supplier')) {
                  list.push('Supplier');
               }
            } else {
               list = list.filter(c => c !== 'Supplier');
            }
            this.profileData.classifications = list;
         }
      },
      combinedAddress: {
         get() {
            if (!this.profileData) return '';
            const parts = [
               this.profileData.address,
               this.profileData.address2,
               this.profileData.city,
               this.profileData.state,
               this.profileData.country,
               this.profileData.postcode
            ];
            return parts.filter(p => p && p.trim()).join(', ');
         },
         set(newValue) {
            if (!this.profileData) return;
            this.profileData.address = newValue;
            this.profileData.address2 = '';
            this.profileData.city = '';
            this.profileData.state = '';
            this.profileData.country = '';
            this.profileData.postcode = '';
         }
      },
      combinedBillingAddress: {
         get() {
            if (!this.profileData) return '';
            const parts = [
               this.profileData.billing_address,
               this.profileData.billing_address2,
               this.profileData.billing_city,
               this.profileData.billing_state,
               this.profileData.billing_country,
               this.profileData.billing_postcode
            ];
            return parts.filter(p => p && p.trim()).join(', ');
         },
         set(newValue) {
            if (!this.profileData) return;
            this.profileData.billing_address = newValue;
            this.profileData.billing_address2 = '';
            this.profileData.billing_city = '';
            this.profileData.billing_state = '';
            this.profileData.billing_country = '';
            this.profileData.billing_postcode = '';
         }
      },
      combinedDeliveryAddress: {
         get() {
            if (!this.profileData) return '';
            const parts = [
               this.profileData.delivery_address,
               this.profileData.delivery_address2,
               this.profileData.delivery_city,
               this.profileData.delivery_state,
               this.profileData.delivery_country,
               this.profileData.delivery_postcode
            ];
            return parts.filter(p => p && p.trim()).join(', ');
         },
         set(newValue) {
            if (!this.profileData) return;
            this.profileData.delivery_address = newValue;
            this.profileData.delivery_address2 = '';
            this.profileData.delivery_city = '';
            this.profileData.delivery_state = '';
            this.profileData.delivery_country = '';
            this.profileData.delivery_postcode = '';
         }
      },
      combinedServiceAddress: {
         get() {
            if (!this.profileData) return '';
            const parts = [
               this.profileData.service_address,
               this.profileData.service_address2,
               this.profileData.service_city,
               this.profileData.service_state,
               this.profileData.service_country,
               this.profileData.service_postcode
            ];
            return parts.filter(p => p && p.trim()).join(', ');
         },
         set(newValue) {
            if (!this.profileData) return;
            this.profileData.service_address = newValue;
            this.profileData.service_address2 = '';
            this.profileData.service_city = '';
            this.profileData.service_state = '';
            this.profileData.service_country = '';
            this.profileData.service_postcode = '';
         }
      },
      filteredOrgUsers() {
          if (!this.orgUsers || !this.clientCommunications) return [];
          const existingUserIds = this.clientCommunications
             .filter(c => c.is_linked && c.user_id)
             .map(c => c.user_id);
          return this.orgUsers.filter(user => !existingUserIds.includes(user.id));
       },
      previewContacts() {
         if (!this.orgContacts || !this.orgContacts.length) return [];
         return this.orgContacts.slice(0, 3);
      },
      filteredOrgContacts() {
         if (!this.orgContacts) return [];
         let list = this.orgContacts;

         if (this.contactsTypeFilter && this.contactsTypeFilter !== 'all') {
            const filterLower = this.contactsTypeFilter.toLowerCase();
            if (filterLower === 'primary') {
               list = list.filter(c => c.is_primary || (c.contact_type && c.contact_type.toLowerCase() === 'primary'));
            } else {
               list = list.filter(c => c.contact_type && c.contact_type.toLowerCase() === filterLower);
            }
         }

         if (this.contactsSearchQuery && this.contactsSearchQuery.trim()) {
            const q = this.contactsSearchQuery.trim().toLowerCase();
            list = list.filter(c => {
               const fullName = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
               const email = (c.email || '').toLowerCase();
               const phone = (c.phone_number || '').toLowerCase();
               const role = (c.role || '').toLowerCase();
               const type = (c.contact_type || '').toLowerCase();
               return fullName.includes(q) || email.includes(q) || phone.includes(q) || role.includes(q) || type.includes(q);
            });
         }

         return list;
      },
      contactTypeCounts() {
         const counts = {
            all: this.orgContacts ? this.orgContacts.length : 0,
            primary: 0,
            lead: 0,
            technical: 0,
            billing: 0,
            signer: 0,
            user: 0
         };
         if (!this.orgContacts) return counts;
         for (const c of this.orgContacts) {
            if (c.is_primary || (c.contact_type && c.contact_type.toLowerCase() === 'primary')) {
               counts.primary++;
            }
            if (c.contact_type) {
               const t = c.contact_type.toLowerCase();
               if (counts[t] !== undefined && t !== 'primary') {
                  counts[t]++;
               }
            }
         }
         return counts;
      }
   },
   watch: {
      selectedLeadId(newVal) {
         if (newVal) {
            this.fetchOrganisationDetails(newVal);
            if (this.$route.query.organisation_uuid !== newVal && this.$route.query.lead_id !== newVal) {
               this.$router.replace({
                  query: { ...this.$route.query, organisation_uuid: newVal }
               }).catch(() => {});
            }
         }
      },
      '$route.query.organisation_uuid'(newVal) {
         if (newVal && newVal !== this.selectedLeadId) {
            this.selectedLeadId = newVal;
         }
      },
      '$route.query.lead_id'(newVal) {
         if (newVal && newVal !== this.selectedLeadId) {
            this.selectedLeadId = newVal;
         }
      },
      'profileData.classifications'(newVal) {
         if (newVal && !newVal.includes('Client') && this.activeTab === 'client_communication') {
            this.activeTab = 'profile';
         }
      }
   },
   methods: {
      navigateToOrganisation(orgUuid) {
         if (!orgUuid) return;
         this.selectedLeadId = orgUuid;
         if (this.activeTab === 'merged_organisations') {
            this.activeTab = 'profile';
         }
      },
      onSearchInput() {
         this.showSearchDropdown = true;
      },
      clearSearchQuery() {
         this.searchQuery = '';
         this.showSearchDropdown = false;
      },
      selectOrgFromDropdown(org) {
         this.showSearchDropdown = false;
         this.searchQuery = '';
         this.selectedLeadId = org.id || org.organisation_uuid;
      },
      toggleSection(sectionName) {
         if (this.sectionOpen[sectionName] !== undefined) {
            this.sectionOpen[sectionName] = !this.sectionOpen[sectionName];
         }
      },
      getMspName(uuid) {
         if (!uuid) return '';
         const item = (this.mspOptions || []).find(o => o.id === uuid || o.uuid === uuid);
         return item ? item.display_name : uuid;
      },
      getPartnerName(uuid) {
         if (!uuid) return '';
         const item = (this.partnerOptions || []).find(o => o.id === uuid || o.uuid === uuid);
         return item ? item.display_name : uuid;
      },
      getEcosystemName(uuid) {
         if (!uuid) return '';
         const item = (this.ecosystemOptions || []).find(o => o.id === uuid || o.uuid === uuid);
         return item ? item.display_name : uuid;
      },
      getOrgClassification(org) {
         if (org && org.classifications && org.classifications.length > 0) {
            const primary = org.classifications.find(c => {
               const val = typeof c === 'string' ? c : (c.classification || '');
               return ['client', 'lead', 'prospect'].includes(val.toLowerCase());
            });
            if (primary) {
               return typeof primary === 'string' ? primary : primary.classification;
            }
            const first = org.classifications[0];
            return typeof first === 'string' ? first : (first.classification || 'Client');
         }
         return 'Client';
      },
      isPartner(org) {
         if (!org) return false;
         if (org.is_partner || org.isPartner) return true;
         if (org.classifications && Array.isArray(org.classifications)) {
            return org.classifications.some(c => typeof c === 'string' ? c.toLowerCase() === 'partner' : (c.classification && c.classification.toLowerCase() === 'partner'));
         }
         return false;
      },
      isSupplier(org) {
         if (!org) return false;
         if (org.is_supplier || org.isSupplier) return true;
         if (org.classifications && Array.isArray(org.classifications)) {
            return org.classifications.some(c => typeof c === 'string' ? c.toLowerCase() === 'supplier' : (c.classification && c.classification.toLowerCase() === 'supplier'));
         }
         return false;
      },
      getVertexLead(org) {
         if (!org) return '';
         if (org.assigned_user) {
            const name = org.assigned_user.name || `${org.assigned_user.first_name || ''} ${org.assigned_user.last_name || ''}`.trim() || org.assigned_user.email;
            if (name) return name;
         }
         if (org.assigned_user_name) return org.assigned_user_name;
         if (org.assigned_user_id && this.bdms && this.bdms.length > 0) {
            const numId = Number(org.assigned_user_id);
            const matched = this.bdms.find(b => b.auth_user_id === numId || b.id === numId || b.db_id === numId);
            if (matched) {
               return matched.name || `${matched.first_name || ''} ${matched.last_name || ''}`.trim() || matched.email || '';
            }
         }
         return '';
      },
      getVertexLeadName() {
         if (!this.profileData) return '';
         if (this.profileData.assigned_user) {
            const name = this.profileData.assigned_user.name || `${this.profileData.assigned_user.first_name || ''} ${this.profileData.assigned_user.last_name || ''}`.trim() || this.profileData.assigned_user.email;
            if (name) return name;
         }
         if (this.profileData.assigned_user_id && this.bdms && this.bdms.length > 0) {
            const numId = Number(this.profileData.assigned_user_id);
            const matched = this.bdms.find(b => b.auth_user_id === numId || b.id === numId || b.db_id === numId);
            if (matched) {
               return matched.name || `${matched.first_name || ''} ${matched.last_name || ''}`.trim() || matched.email || '';
            }
         }
         return '';
      },
      getDisplayFormerNames() {
         if (!this.profileData) return '';
         if (this.profileData.former_names && this.profileData.former_names.length) {
            return this.profileData.former_names.join(', ');
         }
         if (this.profileData.originalOrganisationName && this.profileData.originalOrganisationName !== this.profileData.name) {
            return this.profileData.originalOrganisationName;
         }
         return '';
      },
      handleDocumentClick(e) {
         const container = this.$el.querySelector('.header-search-container');
         if (container && !container.contains(e.target)) {
            this.showSearchDropdown = false;
         }
      },
      normalizePace(val) {
          if (!val) return '';
          if (val.includes('Relaxed') || val.includes('Methodical')) return 'Methodical / Relaxed';
          if (val.includes('Standard') || val.includes('Moderate')) return 'Moderate / Standard';
          if (val.includes('Fast')) return 'Fast-Paced';
          if (val.includes('Urgent') || val.includes('Reactive')) return 'Urgent / Reactive';
          return val;
       },
       normalizeCost(val) {
          if (!val) return '';
          if (val.includes('High')) return 'High (Highly Budget-Conscious)';
          if (val.includes('Moderate')) return 'Moderate (Value-Driven)';
          if (val.includes('Low')) return 'Low (Not a Primary Concern)';
          return val;
       },
       normalizeTech(val) {
          if (!val) return '';
          if (val.includes('Low')) return 'Low (Business-Focused)';
          if (val.includes('Moderate')) return 'Moderate (Tech-Savvy)';
          if (val.includes('High')) return 'High (Expert / Developer Level)';
          return val;
       },
       getPaceBadgeText(val) {
          if (!val) return 'Standard';
          if (val.includes('Relaxed') || val.includes('Methodical')) return 'Relaxed';
          if (val.includes('Standard') || val.includes('Moderate')) return 'Standard';
          if (val.includes('Fast')) return 'Fast';
          if (val.includes('Urgent') || val.includes('Reactive')) return 'Urgent';
          return val;
       },
       getCostBadgeText(val) {
          if (!val) return 'Moderate';
          if (val.includes('High')) return 'High';
          if (val.includes('Moderate')) return 'Moderate';
          if (val.includes('Low')) return 'Low';
          return val;
       },
       getTechBadgeText(val) {
          if (!val) return 'Moderate';
          if (val.includes('Low')) return 'Low';
          if (val.includes('Moderate')) return 'Moderate';
          if (val.includes('High')) return 'High';
          return val;
       },
       getPaceBadgeClass(val) {
          if (!val) return 'comm-badge--intensity-medium';
          if (val.includes('Relaxed') || val.includes('Methodical')) return 'comm-badge--intensity-low';
          if (val.includes('Standard') || val.includes('Moderate')) return 'comm-badge--intensity-medium';
          if (val.includes('Fast')) return 'comm-badge--intensity-high';
          if (val.includes('Urgent') || val.includes('Reactive')) return 'comm-badge--intensity-critical';
          return 'comm-badge--intensity-medium';
       },
       getCostBadgeClass(val) {
          if (!val) return 'comm-badge--intensity-medium';
          if (val.includes('Low')) return 'comm-badge--intensity-low';
          if (val.includes('Moderate')) return 'comm-badge--intensity-medium';
          if (val.includes('High')) return 'comm-badge--intensity-high';
          return 'comm-badge--intensity-medium';
       },
       getTechBadgeClass(val) {
          if (!val) return 'comm-badge--intensity-medium';
          if (val.includes('Low')) return 'comm-badge--intensity-low';
          if (val.includes('Moderate')) return 'comm-badge--intensity-medium';
          if (val.includes('High')) return 'comm-badge--intensity-high';
          return 'comm-badge--intensity-medium';
       },
       getPaceExplainer(value) {
          if (!value) return '';
          if (value.includes('Relaxed') || value.includes('Methodical')) {
             return "Prefers thoroughness over speed. Needs time to review, digest information, and consult internally. Rarely pushes for urgent deadlines.";
          }
          if (value.includes('Standard') || value.includes('Moderate')) {
             return "Expects typical, predictable turnaround times. Balances speed with quality and respects standard SLA response times.";
          }
          if (value.includes('Fast')) {
             return "High energy and expects quick responses/rapid turnarounds. Prefers to keep momentum high and take immediate action.";
          }
          if (value.includes('Urgent') || value.includes('Reactive')) {
             return "Frequently operates in rush mode. Requires immediate replies, expects team to drop everything for their requests, and pushes aggressive deadlines.";
          }
          return "";
       },
       getCostExplainer(value) {
          if (!value) return '';
          if (value.includes('High')) {
             return "Every dollar counts. Requires strict, detailed justification for all costs. Will actively seek the cheapest option and frequently push back on pricing.";
          }
          if (value.includes('Moderate')) {
             return "Will spend money if the Return on Investment (ROI) or long-term value is clearly demonstrated. Looks for fair pricing rather than the absolute cheapest option.";
          }
          if (value.includes('Low')) {
             return "Quality, speed, or specific technical outcomes trump cost. Willing to authorise premium rates for the right result, expert advice, or reduced friction.";
          }
          return "";
       },
       getTechExplainer(value) {
          if (!value) return '';
          if (value.includes('Low')) {
             return "Non-technical. Needs concepts explained in plain English, focusing on business outcomes and ROI. Avoid jargon, acronyms, or deep architecture discussions.";
          }
          if (value.includes('Moderate')) {
             return "Understands general technical concepts and how systems interact at a high level. Comfortable with tech terminology but does not need to see the underlying code.";
          }
          if (value.includes('High')) {
             return "Deeply technical. Wants to see architecture diagrams, understand the exact tech stack, discuss security protocols, and review implementation details.";
          }
          return "";
       },
       getPaceBadgeTooltip(val) {
          const explainer = this.getPaceExplainer(val);
          return explainer ? `Client Pace Preference: ${explainer}` : 'Client Pace Preference';
       },
       getCostBadgeTooltip(val) {
          const explainer = this.getCostExplainer(val);
          return explainer ? `Client Cost Sensitivity: ${explainer}` : 'Client Cost Sensitivity';
       },
       getTechBadgeTooltip(val) {
          const explainer = this.getTechExplainer(val);
          return explainer ? `Client Technicality: ${explainer}` : 'Client Technicality';
       },
       getUserInitials(name) {
          if (!name) return 'U';
          const parts = name.trim().split(/\s+/);
          if (parts.length >= 2) {
             return (parts[0][0] + parts[1][0]).toUpperCase();
          }
          return name.substring(0, 2).toUpperCase();
       },
       getAvatarStyle(name) {
          if (!name) name = 'User';
          const colors = ['#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#06b6d4', '#f43f5e'];
          let hash = 0;
          for (let i = 0; i < name.length; i++) {
             hash = name.charCodeAt(i) + ((hash << 5) - hash);
          }
          const color = colors[Math.abs(hash) % colors.length];
          return `background-color: ${color}15; color: ${color}; font-weight: 700; width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 1.05rem; border: 1.5px solid ${color}30;`;
       },
      async getBdms() {
         try {
            const bdmResp = await this.$axios.get('/vertex-bdm');
            if (bdmResp.data && bdmResp.data.success) {
               this.bdms = bdmResp.data.bdms || [];
            }
         } catch (error) {
            console.error('Error fetching BDMs inside dashboard:', error);
         }
      },
      async getVertexUsers() {
         let users = this.$store.state.auth && this.$store.state.auth.usersWithVertexRoles;
         if (!users || !users.length) {
            try {
               const response = await this.$axios.get("/auth/users/super-admin");
               users = response.data.super_admin_users || [];
               this.$store.commit("auth/setUsersWithVertexRoles", users);
            } catch (error) {
               console.error("Error fetching super admin users in dashboard:", error);
               users = [];
            }
         }
         this.vertexUsers = (users || []).map(item => ({
            id: item.id,
            first_name: item.first_name || '',
            last_name: item.last_name || '',
            email: item.email || '',
            name: `${item.first_name || ''} ${item.last_name || ''}`.trim() || item.email || ''
         }));
      },
      async fetchDropdownCategories() {
         try {
            const mspRes = await this.$axios.get("/categories?name=MSP");
            if (mspRes.data && mspRes.data.category && mspRes.data.category.subcategories) {
               this.mspOptions = mspRes.data.category.subcategories;
            }
            const partnerRes = await this.$axios.get("/categories?name=Partner");
            if (partnerRes.data && partnerRes.data.category && partnerRes.data.category.subcategories) {
               this.partnerOptions = partnerRes.data.category.subcategories;
            }
            const ecosystemRes = await this.$axios.get("/categories?name=Ecosystem");
            if (ecosystemRes.data && ecosystemRes.data.category && ecosystemRes.data.category.subcategories) {
               this.ecosystemOptions = ecosystemRes.data.category.subcategories;
            }
         } catch (err) {
            console.error('Error fetching categories for dropdowns:', err);
         }
      },
      async fetchLeads() {
         this.isLoading = true;
         try {
            const response = await this.$axios.get('/organisations?pageSize=10000&page=1&excludeTestData=false&includeMerged=true');
            if (response.data && response.data.organisations) {
               this.leads = response.data.organisations.map(org => {
                  const classifications = org.classifications ? org.classifications.map(c => c.classification) : [];
                  let assignedUserName = '';
                  if (org.assigned_user) {
                     assignedUserName = org.assigned_user.name || `${org.assigned_user.first_name || ''} ${org.assigned_user.last_name || ''}`.trim() || org.assigned_user.email || '';
                  }
                  return {
                     id: org.id,
                     uuid: org.uuid || org.id,
                     organisation_uuid: org.uuid || org.id,
                     organisation_name: org.name,
                     name: org.name,
                     abn: org.abn,
                     email: org.email,
                     is_merged: org.is_merged || false,
                     merged_into_uuid: org.merged_into_uuid || '',
                     merged_into_name: org.merged_into_name || '',
                     created_at: org.date_created,
                     classifications: classifications,
                     is_partner: classifications.includes('Partner') || org.is_partner || false,
                     is_supplier: classifications.includes('Supplier') || org.is_supplier || false,
                     assigned_user_id: org.assigned_user_id,
                     assigned_user: org.assigned_user,
                     assigned_user_name: assignedUserName,
                     former_names: org.former_names || [],
                  };
               });
               
               const queryOrgId = this.$route.query.organisation_uuid || this.$route.query.lead_id;
               if (queryOrgId && this.leads.some(l => l.id === queryOrgId)) {
                  this.selectedLeadId = queryOrgId;
               } else if (this.leads.length > 0) {
                  this.selectedLeadId = this.leads[0].id;
               }
            }
         } catch (err) {
            console.error('Error fetching organisations:', err);
            this.$toast.error('Could not load organisation list.');
         } finally {
            this.isLoading = false;
         }
      },
      async fetchOrganisationDetails(uuid) {
         if (!uuid) return;
         this.isLoading = true;
         try {
            let org = null;
            let lead = null;
            let leadUuid = null;

            // 1. Try fetching as Organisation
            try {
               const orgResponse = await this.$axios.get(`/organisations/${uuid}`);
               if (orgResponse.data && orgResponse.data.organisation) {
                  org = orgResponse.data.organisation;
                  leadUuid = org.lead_uuid;
               }
            } catch (err) {
               console.log('UUID is not an organisation, trying lead fallback...', err);
            }

            // 2. If not found, try fetching as Lead
            if (!org) {
               try {
                  const leadResponse = await this.$axios.get(`/leads/${uuid}`);
                  if (leadResponse.data && leadResponse.data.lead) {
                     lead = leadResponse.data.lead;
                     leadUuid = lead.id;
                     if (lead.organisation_uuid) {
                        const orgResponse = await this.$axios.get(`/organisations/${lead.organisation_uuid}`);
                        if (orgResponse.data && orgResponse.data.organisation) {
                           org = orgResponse.data.organisation;
                        }
                     }
                  }
               } catch (err) {
                  console.error('Failed to fetch lead fallback:', err);
               }
            } else if (leadUuid) {
               // Fetch lead details for contact names/statuses
               try {
                  const leadResponse = await this.$axios.get(`/leads/${leadUuid}`);
                  if (leadResponse.data && leadResponse.data.lead) {
                     lead = leadResponse.data.lead;
                  }
               } catch (err) {
                  console.warn('Could not fetch associated lead:', err);
               }
            }

            // If we still don't have an organisation but have a lead, build a mock org from the lead
            if (!org && lead) {
               org = {
                  id: lead.organisation_uuid || lead.id,
                  name: lead.organisation_name || '',
                  abn: lead.abn || '',
                  email: lead.email || '',
                  phone_number: lead.phone_number || '',
                  address: lead.country_code || '',
                  background: lead.background || '',
                  is_charity: lead.is_charity || false,
                  is_test_data: lead.is_test_data || false,
                  is_msp_managed: false,
                  msp_uuid: '',
                  is_partner_referred: false,
                  partner_uuid: '',
                  is_ecosystem: false,
                  ecosystem_uuid: '',
                  classifications: lead.classification ? lead.classification.split(',').map(c => ({ classification: c.trim() })) : []
               };
            }

            if (!org) {
               throw new Error('Organisation record not found');
            }

            const classifications = org.classifications ? org.classifications.map(c => c.classification) : [];

            this.profileData = {
               id: org.id,
               uuid: org.id,
               classifications: classifications || [],
               name: org.name || '',
               abn: org.abn || '',
                firstName: (org && org.org_lead_first_name) ? org.org_lead_first_name : (lead ? (lead.first_name || '') : ''),
                lastName: (org && org.org_lead_last_name) ? org.org_lead_last_name : (lead ? (lead.last_name || '') : ''),
                leadName: (org && (org.org_lead_first_name || org.org_lead_last_name))
                   ? `${org.org_lead_first_name || ''} ${org.org_lead_last_name || ''}`.trim()
                   : ((lead && `${lead.first_name || ''} ${lead.last_name || ''}`.trim()) ? `${lead.first_name || ''} ${lead.last_name || ''}`.trim() : ''),
                phone: (org && (org.org_phone_number || org.phone_number))
                   ? (org.org_phone_number || org.phone_number)
                   : (lead ? (lead.phone_number || '') : ''),
                email: (org && org.email) ? org.email : '',
                org_lead_email: (org && org.org_lead_email) ? org.org_lead_email : (lead ? (lead.email || '') : ''),
                org_lead_first_name: (org && org.org_lead_first_name) ? org.org_lead_first_name : (lead ? (lead.first_name || '') : ''),
                org_lead_last_name: (org && org.org_lead_last_name) ? org.org_lead_last_name : (lead ? (lead.last_name || '') : ''),
                org_lead_phone: (org && org.org_lead_phone) ? org.org_lead_phone : (lead ? (lead.phone_number || '') : ''),
               isCharity: org.is_charity || false,
               address: org.address || '',
               address2: org.address2 || '',
               city: org.city || '',
               state: org.state || '',
               country: org.country || '',
               postcode: org.postcode || '',
               billing_address: org.billing_address || '',
               billing_address2: org.billing_address2 || '',
               billing_city: org.billing_city || '',
               billing_state: org.billing_state || '',
               billing_country: org.billing_country || '',
               billing_postcode: org.billing_postcode || '',
               billing_email: org.billing_email || '',
               delivery_address: org.delivery_address || '',
               delivery_address2: org.delivery_address2 || '',
               delivery_city: org.delivery_city || '',
               delivery_state: org.delivery_state || '',
               delivery_country: org.delivery_country || '',
               delivery_postcode: org.delivery_postcode || '',
               service_address: org.service_address || '',
               service_address2: org.service_address2 || '',
               service_city: org.service_city || '',
               service_state: org.service_state || '',
               service_country: org.service_country || '',
               service_postcode: org.service_postcode || '',
               service_email: org.service_email || '',
               primary_contact_first_name: org.primary_contact_first_name || '',
               primary_contact_last_name: org.primary_contact_last_name || '',
               primary_contact_email: org.primary_contact_email || '',
               primary_contact_phone: org.primary_contact_phone || '',
               background: org.background || '',
               firstContactDate: this.formatDate(org.auth_creation_date || org.created_at || org.date_created),
               clientType: classifications.includes('Client') ? 'Client' : (classifications.includes('Prospect') ? 'Prospect' : 'Lead'),
               isTestData: org.is_test_data || false,
               isAuthSynced: org.is_auth_synced || false,
               isSupplier: classifications.includes('Supplier'),
               isPartner: classifications.includes('Partner'),
               currency: org.currency || 'AUD',
               assigned_user_id: Number(org.assigned_user_id || (lead ? lead.assigned_user_id : 0) || 0),
               assigned_user: org.assigned_user || (lead ? lead.assigned_user : null) || null,
               vertex_org_manager_id: Number(org.vertex_org_manager_id || 0),
               clients: ['Vertex Cyber Sec', 'Vertex Cyber Secure', 'Vertex Cyber Security'],
               suppliers: ['Vertex Cyber Sec', 'Vertex Cyber Secure', 'Vertex Cyber Security'],
               partners: ['Partner 1'],
               leadTypeId: lead ? lead.lead_type_id : '',
               leadStatusId: lead ? lead.lead_status_id : '',
               leadPriorityId: lead ? lead.lead_priority_id : '',
               leadUuid: leadUuid || '',
               hasAuthOrganisation: org.has_auth_organisation || 0,
               authOrganisationName: org.auth_organisation_name || "",
               authOrganisationUuid: org.auth_organisation_uuid || "",
                authOrganisationId: org.auth_organisation_id || 0,
                originalOrganisationName: org.original_organisation_name || (lead ? lead.original_organisation_name : '') || (org.pre_link_snapshot ? (() => { try { return JSON.parse(org.pre_link_snapshot).original_organisation_name } catch(e){ return '' } })() : '') || '',
                syncedAt: org.synced_at || null,
               organisationUuid: org.organisation_uuid || org.id,
               xeroContactId: org.xero_contact_id || "",
               isMspManaged: org.is_msp_managed || false,
               mspUuid: org.msp_uuid || '',
               isPartnerReferred: org.is_partner_referred || false,
               partnerUuid: org.partner_uuid || '',
               isEcosystem: org.is_ecosystem || false,
               ecosystemUuid: org.ecosystem_uuid || '',
               is_merged: org.is_merged || false,
               merged_into_uuid: org.merged_into_uuid || '',
               merged_into_name: org.merged_into_name || '',
               merged_at: org.merged_at || null,
               merged_by: org.merged_by || 0,
               merged_by_name: org.merged_by_name || '',
               merge_reason: org.merge_reason || '',
               merge_snapshot: org.merge_snapshot || '',
               former_names: org.former_names || []
            };

            this.initialProfileData = JSON.parse(JSON.stringify(this.profileData));
            
            await this.fetchCombinedTimeline(org.id);

            if (this.profileData.hasAuthOrganisation == 1 && this.profileData.authOrganisationId) {
               await this.fetchOrgUsers(this.profileData.authOrganisationId);
            } else {
               this.orgUsers = [];
            }
            await this.fetchClientCommunications(org.id);
            await this.fetchOrganisationContacts();
            await this.fetchMergedOrganisations(org.id);
         } catch (err) {
            console.error('Error fetching organisation/lead details:', err);
            this.$toast.error('Could not load organisation details.');
         } finally {
            this.isLoading = false;
         }
      },
      async fetchOrganisationContacts(forceSync = false) {
         const orgId = (this.profileData && (this.profileData.id || this.profileData.uuid)) || this.selectedLeadId;
         if (!orgId) return;

         if (forceSync) {
            this.isContactsSyncing = true;
         } else {
            this.isContactsLoading = true;
         }

         try {
            const url = `/organisations/${orgId}/contacts${forceSync ? '?sync=true' : ''}`;
            const res = await this.$axios.get(url);
            if (res.data && res.data.contacts) {
               this.orgContacts = res.data.contacts;
               if (forceSync) {
                  this.$toast.success(res.data.message || 'Contacts synced successfully.');
               }
            } else {
               this.orgContacts = [];
            }
         } catch (err) {
            console.error('Error fetching organisation contacts:', err);
            if (forceSync) {
               this.$toast.error('Failed to sync contacts.');
            }
         } finally {
            this.isContactsLoading = false;
            this.isContactsSyncing = false;
         }
      },
      async syncOrganisationContacts() {
         if (this.profileData.hasAuthOrganisation != 1) {
            this.$toast.warning("Please link this client to an Auth organisation first to enable sync.");
            return;
         }
         await this.fetchOrganisationContacts(true);
      },
      getContactTypeBadgeClass(type) {
         if (!type) return 'contact-type-badge-default';
         const t = type.toLowerCase().trim();
         switch (t) {
            case 'lead':
               return 'contact-type-badge-lead';
            case 'primary':
               return 'contact-type-badge-primary';
            case 'technical':
               return 'contact-type-badge-technical';
            case 'billing':
               return 'contact-type-badge-billing';
            case 'signer':
               return 'contact-type-badge-signer';
            case 'user':
               return 'contact-type-badge-user';
            default:
               return 'contact-type-badge-default';
         }
      },
      getContactInitials(firstName, lastName, email) {
         const f = (firstName || '').trim();
         const l = (lastName || '').trim();
         if (f && l) {
            return (f[0] + l[0]).toUpperCase();
         }
         if (f) return f.slice(0, 2).toUpperCase();
         if (l) return l.slice(0, 2).toUpperCase();
         if (email) return email.slice(0, 2).toUpperCase();
         return 'C';
      },
      async copyContactEmail(email) {
         if (!email) return;
         try {
            await navigator.clipboard.writeText(email);
            this.copiedContactEmail = email;
            this.$toast.success(`Copied ${email} to clipboard!`);
            setTimeout(() => {
               if (this.copiedContactEmail === email) {
                  this.copiedContactEmail = null;
               }
            }, 2500);
         } catch (err) {
            const el = document.createElement('textarea');
            el.value = email;
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            this.copiedContactEmail = email;
            this.$toast.success(`Copied ${email} to clipboard!`);
            setTimeout(() => {
               if (this.copiedContactEmail === email) {
                  this.copiedContactEmail = null;
               }
            }, 2500);
         }
      },
      createProposalForContact(contact) {
         const orgId = (this.profileData && (this.profileData.id || this.profileData.uuid || this.profileData.organisationUuid)) || this.selectedLeadId;
         const query = {
            create: 'true'
         };
         if (orgId) {
            query.org_id = orgId;
         }
         if (this.profileData && this.profileData.leadUuid) {
            query.lead_id = this.profileData.leadUuid;
         }
         if (contact) {
            if (contact.id) query.contact_id = String(contact.id);
            if (contact.uuid) query.contact_uuid = String(contact.uuid);
            if (contact.email) query.contact_email = String(contact.email);
         }
         this.$router.push({ path: '/proposals', query });
      },
      resolveTimelineUser(userId) {
         if (!userId || userId === 0 || userId === '0') {
            return { isSystem: true, name: 'System Generated' };
         }

         const numericId = parseInt(userId);

         // 1. Check currentUser
         const currentUser = this.$store.state.auth && this.$store.state.auth.currentUser;
         if (currentUser && (currentUser.id === numericId || currentUser.auth_user_id === numericId)) {
            const name = `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.name || currentUser.email;
            if (name) return { isSystem: false, name };
         }

         // 2. Check usersWithVertexRoles in store
         const usersWithRoles = (this.$store.state.auth && this.$store.state.auth.usersWithVertexRoles) || [];
         if (Array.isArray(usersWithRoles)) {
            const matchedRoleUser = usersWithRoles.find(u => u.id === numericId || u.auth_user_id === numericId);
            if (matchedRoleUser) {
               const name = `${matchedRoleUser.first_name || ''} ${matchedRoleUser.last_name || ''}`.trim() || matchedRoleUser.name || matchedRoleUser.email;
               if (name) return { isSystem: false, name };
            }
         }

         // 3. Check bdms list
         if (this.bdms && Array.isArray(this.bdms)) {
            const matchedBdm = this.bdms.find(b => b.auth_user_id === numericId || b.id === numericId || b.user_id === numericId);
            if (matchedBdm) {
               const name = matchedBdm.name || `${matchedBdm.first_name || ''} ${matchedBdm.last_name || ''}`.trim() || matchedBdm.email;
               if (name) return { isSystem: false, name };
            }
         }

         // 4. Check orgUsers if available
         if (this.orgUsers && Array.isArray(this.orgUsers)) {
            const matchedOrgUser = this.orgUsers.find(u => u.id === numericId);
            if (matchedOrgUser && matchedOrgUser.name) {
               return { isSystem: false, name: matchedOrgUser.name };
            }
         }

         return { isSystem: false, name: `User #${numericId}` };
      },
      async fetchCombinedTimeline(orgUuid) {
         if (!orgUuid) {
            this.timelineEvents = [];
            return;
         }
         try {
            const response = await this.$axios.get(`/organisations/${orgUuid}/combined-timeline`);
            if (response.data && response.data.lead_notes) {
               const notes = response.data.lead_notes;
               
               this.timelineEvents = notes.map(note => {
                  const type = note.note_type || '';
                  let image = null;
                  if (type.includes('auth') || type === 'auth_org_link') {
                     image = require('@/assets/img/vertex_products_logo/Auth.svg');
                  } else if (type.includes('proposal') || type === 'proposal_sent' || type === 'proposal_accepted') {
                     image = require('@/assets/img/S.svg');
                  }

                  let isSystem = false;
                  let userName = '';
                  if (note.is_system !== undefined && note.is_system !== null) {
                     isSystem = !!note.is_system;
                     userName = note.user_name || (isSystem ? 'System Generated' : `User #${note.created_by}`);
                  } else {
                     const userInfo = this.resolveTimelineUser(note.created_by);
                     isSystem = userInfo.isSystem;
                     userName = userInfo.name;
                  }

                  let title = this.getNoteTypeName(type);
                  if (type !== 'classification' && type !== 'org_update' && type !== 'client_communication' && !title.endsWith('Update') && !title.endsWith('Updated') && !title.endsWith('Linkage') && !title.endsWith('Generated') && !title.endsWith('Sent') && !title.endsWith('Signed')) {
                     title += ' Event';
                  }

                  return {
                     id: note.id || note.uuid,
                     time: moment(note.date_created || note.created_at).format('D-MMM-YYYY h:mm A'),
                     title: title,
                     description: this.formatNoteDetails(note.note_details, type),
                     icon: this.getNoteTypeIcon(type),
                     image: image,
                     isSystem: isSystem,
                     userName: userName
                  };
               });
            } else {
               this.timelineEvents = [];
            }
         } catch (err) {
            console.error('Error fetching timeline:', err);
            this.timelineEvents = [];
         }
      },
      formatNoteDetails(details, type) {
         if (!details) return '';
         if (type === 'classification') {
            const parts = details.split(',').map(p => p.trim());
            const primaryClasses = ['Client', 'Prospect', 'Lead'];
            const primaryParts = parts.filter(p => primaryClasses.includes(p));
            if (primaryParts.length >= 2) {
               return `${primaryParts[0]} ➔ ${primaryParts[1]}`;
            } else if (primaryParts.length === 1) {
               return primaryParts[0];
            }
            return details;
         }
         if (type && (type.includes('status') || type.includes('priority'))) {
            const parts = details.split(',').map(p => p.trim());
            const formattedParts = parts.map(part => {
               // Add space before capital letters in camelCase
               return part.replace(/([a-z])([A-Z])/g, '$1 $2');
            });
            return formattedParts.join(' ➔ ');
         }
         return details;
      },
      getNoteTypeName(type) {
         if (!type) return 'Note';
         if (type === 'classification') return 'Organisation Type Updated';
         if (type === 'lead_generated' || type.includes('initial_status')) return 'Lead Generated';
         if (type === 'proposal_sent') return 'Proposal Sent';
         if (type === 'proposal_accepted') return 'Proposal Accepted';
         if (type === 'contract_sent') return 'Contract Sent';
         if (type === 'contract_accepted') return 'Contract Signed';
         if (type === 'auth_org_link') return 'Auth Organisation Linkage';
         if (type === 'client_communication') return 'Client Communication';
         if (type === 'org_update') return 'Organisation Profile Updated';
         if (type === 'org_merge') return 'Organisation Merged';
         if (type === 'org_unmerge') return 'Organisation Unmerged';

         if (type.includes('status')) return 'Status Update';
         if (type.includes('priority')) return 'Priority Update';
         if (type.includes('proposal')) return 'Proposal';
         if (type.includes('contract')) return 'Contract';
         return 'Note';
      },
      getNoteTypeIcon(type) {
         if (!type) return 'fas fa-sticky-note text-secondary';
         if (type === 'classification') return 'fas fa-building text-info';
         if (type === 'lead_generated' || type.includes('initial_status')) return 'fas fa-user-plus text-primary';
         if (type === 'proposal_sent') return 'fas fa-paper-plane text-info';
         if (type === 'proposal_accepted') return 'fas fa-check-circle text-success';
         if (type === 'contract_sent') return 'fas fa-file-contract text-warning';
         if (type === 'contract_accepted') return 'fas fa-signature text-success';
         if (type === 'auth_org_link') return 'fas fa-shield-alt text-primary';
         if (type === 'client_communication') return 'fas fa-comments text-primary';
         if (type === 'org_update') return 'fas fa-id-card text-info';
         if (type === 'org_merge') return 'fas fa-code-branch text-warning';
         if (type === 'org_unmerge') return 'fas fa-undo text-success';

         if (type.includes('status')) return 'fas fa-tasks text-warning';
         if (type.includes('priority')) return 'fas fa-exclamation-circle text-danger';
         if (type.includes('proposal')) return 'fas fa-file-invoice text-info';
         if (type.includes('contract')) return 'fas fa-file-signature text-success';
         return 'fas fa-sticky-note text-secondary';
      },

      openDeleteOrgModal() {
         this.showDeleteOrgModal = true;
      },
      closeDeleteOrgModal() {
         this.showDeleteOrgModal = false;
      },
      async deleteOrganisation() {
         const uuid = this.profileData.uuid || this.profileData.id;
         if (!uuid) return;
         this.isDeleting = true;
         try {
            const response = await this.$axios.delete(`/organisations/${uuid}`);
            if (response.data.success) {
               this.$toast.success(response.data.message || 'Organisation deleted successfully.');
               this.closeDeleteOrgModal();
               this.$router.push({ name: 'clients-view' });
            } else {
               this.$toast.error(response.data.message || 'Failed to delete organisation.');
            }
         } catch (error) {
            const msg = error.response && error.response.data && error.response.data.message
               ? error.response.data.message
               : 'Error deleting organisation';
            this.$toast.error(msg);
         } finally {
            this.isDeleting = false;
         }
      },
      openDeactivateOrgModal() {
         this.showDeactivateOrgModal = true;
      },
      closeDeactivateOrgModal() {
         this.showDeactivateOrgModal = false;
      },
      async deactivateOrganisation() {
         const uuid = this.profileData.uuid || this.profileData.id;
         if (!uuid) return;
         this.isDeactivating = true;
         try {
            const response = await this.$axios.delete(`/organisations/${uuid}`);
            if (response.data.success) {
               this.$toast.success('Organisation deactivated successfully.');
               this.closeDeactivateOrgModal();
               this.$router.push({ name: 'clients-view' });
            } else {
               this.$toast.error(response.data.message || 'Failed to deactivate organisation.');
            }
         } catch (error) {
            const msg = error.response && error.response.data && error.response.data.message
               ? error.response.data.message
               : 'Error deactivating organisation';
             this.$toast.error(msg);
         } finally {
             this.isDeactivating = false;
         }
      },
      openInAuth() {
         if (this.profileData.hasAuthOrganisation == 1 && this.profileData.authOrganisationId) {
            const authBaseUrl = this.$config.getAuthBaseUrl();
            const accessToken = this.$store.state.auth.accessToken;
            const orgId = this.profileData.authOrganisationId;
            const url = `${authBaseUrl}/organisations/${orgId}/settings/?access_token=${accessToken}&org_selected=${orgId}`;
            window.open(url, '_blank');
         } else {
            this.$toast.warning("This organisation is not linked to Auth yet. Please link it first under 'Auth Organisation Linkage'.");
         }
      },
      openInXero() {
         if (this.profileData.hasAuthOrganisation == 1) {
            const xeroBaseUrl = process.env.VUE_APP_XERO_BASE_URL || 'https://go.xero.com';
            const shortCode = process.env.VUE_APP_XERO_SHORT_CODE || '!skYq5';
            const contactId = this.profileData.xeroContactId;
            const url = contactId 
               ? `${xeroBaseUrl}/app/${shortCode}/contacts/contact/${contactId}`
               : `${xeroBaseUrl}/Organisation/Dashboard`;
            window.open(url, '_blank');
         } else {
            this.$toast.warning("This organisation is not linked to Auth/Xero yet. Please link it first to enable Xero integration.");
         }
      },
      async manualSyncFromAuth() {
         const orgId = this.profileData.authOrganisationId;
         if (!orgId) {
            this.$toast.warning("Please link this client to an Auth organisation first to enable manual sync.");
            return;
         }

         this.isSyncing = true;
         try {
            const response = await this.$axios.post(`/auth/organisations/${orgId}/sync`);
            if (response.data && response.data.success) {
               this.$toast.success(response.data.message || "Organisation synced successfully.");
               const uuid = this.profileData.uuid || this.profileData.id;
               await this.fetchOrganisationDetails(uuid);
            } else {
               this.$toast.error(response.data.message || "Failed to sync organisation.");
            }
         } catch (error) {
            console.error("Sync error:", error);
            const msg = error.response && error.response.data && error.response.data.message ? error.response.data.message : 'Error syncing organisation details.';
            this.$toast.error(msg);
         } finally {
            this.isSyncing = false;
         }
      },
      openMarkAuthModal() {
         this.authMarkModal = {
            show: true,
            type: 'mark',
            isSubmitting: false
         };
      },
      openUnmarkAuthModal() {
         this.authMarkModal = {
            show: true,
            type: 'unmark',
            isSubmitting: false
         };
      },
      closeAuthMarkModal() {
         this.authMarkModal.show = false;
         this.authMarkModal.isSubmitting = false;
      },
      async confirmAuthMarkAction() {
         const isMark = this.authMarkModal.type === 'mark';
         const uuid = this.profileData.uuid || this.profileData.id;
         const orgData = {
            auth_organisation_id: 0,
            has_auth_organisation: isMark ? 2 : 0,
            auth_organisation_name: "",
            auth_organisation_uuid: "",
            xero_contact_id: "",
            marked_auth_creation: isMark
         };

         this.authMarkModal.isSubmitting = true;
         try {
            const response = await this.$axios.put(`/organisations/${uuid}/auth/organisation`, orgData);
            if (response.data.success) {
               this.$toast.success(response.data.message || (isMark ? 'Marked for Auth creation' : 'Unmarked from Auth creation'));
               this.closeAuthMarkModal();
               this.fetchOrganisationDetails(uuid);
            } else {
               this.$toast.error(response.data.message);
            }
         } catch (error) {
            const msg = error.response && error.response.data && error.response.data.message ? error.response.data.message : `Error ${isMark ? 'marking' : 'unmarking'} organisation`;
            this.$toast.error(msg);
         } finally {
            this.authMarkModal.isSubmitting = false;
         }
      },
      addPartner() {
         if (this.selectedPartnerToAdd && !this.profileData.partners.includes(this.selectedPartnerToAdd)) {
            this.profileData.partners.push(this.selectedPartnerToAdd);
            this.$toast.success(`Linked partner: ${this.selectedPartnerToAdd}`);
         }
         this.selectedPartnerToAdd = '';
      },
       removePartner(partner) {
          this.profileData.partners = this.profileData.partners.filter(p => p !== partner);
          this.$toast.info(`Unlinked partner: ${partner}`);
       },
       async fetchOrgUsers(authOrgId) {
          if (!authOrgId) {
             this.orgUsers = [];
             return;
          }
          this.isLoadingUsers = true;
          try {
             const response = await this.$axios.get(`/auth/organisations/${authOrgId}/users`);
             if (response.data && response.data.users) {
                this.orgUsers = response.data.users.map(u => ({
                   id: u.id,
                   name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
                   email: u.email
                }));
             } else {
                this.orgUsers = [];
             }
          } catch (err) {
             console.error('Error fetching org users:', err);
             this.orgUsers = [];
          } finally {
             this.isLoadingUsers = false;
          }
       },
       async fetchClientCommunications(orgUuid) {
          if (!orgUuid) {
             this.clientCommunications = [];
             return;
          }
          try {
             const response = await this.$axios.get(`/organisations/${orgUuid}/client-communications`);
             if (response.data && response.data.client_communications) {
                this.clientCommunications = response.data.client_communications;
             } else {
                this.clientCommunications = [];
             }
          } catch (err) {
             console.error('Error fetching client communications:', err);
             this.clientCommunications = [];
          }
       },
       getUserName(userId) {
          const user = this.orgUsers.find(u => u.id === userId);
          return user ? user.name : `User ID: ${userId}`;
       },
       getUserEmail(userId) {
          const user = this.orgUsers.find(u => u.id === userId);
          return user ? user.email : '';
       },
        editCommPrefs(comm) {
          this.editingCommId = comm.id;
          if (comm.is_linked) {
             const user = this.orgUsers.find(u => u.id === comm.user_id);
             this.selectedUsers = user ? [user] : [{ id: comm.user_id, name: comm.name || `User ID: ${comm.user_id}` }];
             this.commForm = {
                name: '',
                clientPace: this.normalizePace(comm.client_pace || ''),
                costSensitivity: this.normalizeCost(comm.cost_sensitivity || ''),
                technicality: this.normalizeTech(comm.technicality || ''),
                decisionStyle: comm.decision_style || '',
                communicationStyle: comm.communication_style || ''
             };
          } else {
             this.selectedUsers = [];
             this.commForm = {
                name: comm.name || '',
                clientPace: this.normalizePace(comm.client_pace || ''),
                costSensitivity: this.normalizeCost(comm.cost_sensitivity || ''),
                technicality: this.normalizeTech(comm.technicality || ''),
                decisionStyle: comm.decision_style || '',
                communicationStyle: comm.communication_style || ''
             };
          }
       },
       cancelEditComm() {
         this.editingCommId = null;
         this.selectedUsers = [];
         this.commForm = {
            name: '',
            clientPace: '',
            costSensitivity: '',
            technicality: '',
            decisionStyle: '',
            communicationStyle: ''
         };
      },
      openCreateCommModal() {
         this.cancelEditComm();
         this.showCommModal = true;
      },
      closeCommModal() {
         this.cancelEditComm();
         this.showCommModal = false;
      },
       async saveCommPrefs() {
          const isLinked = this.profileData.hasAuthOrganisation == 1;
          let users = [];

          if (isLinked) {
             if (this.selectedUsers.length === 0) return;
             users = this.selectedUsers.map(u => ({ id: u.id, name: u.name }));
          } else {
             if (!this.commForm.name || !this.commForm.name.trim()) return;
             users = [{ id: 0, name: this.commForm.name.trim() }];
          }

          const payload = {
             id: this.editingCommId || 0,
             is_linked: isLinked,
             users: users,
             client_pace: this.commForm.clientPace,
             cost_sensitivity: this.commForm.costSensitivity,
             technicality: this.commForm.technicality,
             decision_style: this.commForm.decisionStyle,
             communication_style: this.commForm.communicationStyle
          };

          this.isLoading = true;
          try {
             const orgUuid = this.profileData.uuid || this.profileData.id;
             const response = await this.$axios.post(`/organisations/${orgUuid}/client-communications`, payload);
             if (response.data.success) {
                this.$toast.success(response.data.message);
                this.cancelEditComm();
                this.showCommModal = false;
                await this.fetchClientCommunications(orgUuid);
                await this.fetchCombinedTimeline(orgUuid);
             } else {
                this.$toast.error(response.data.message);
             }
          } catch (err) {
             console.error('Error saving comm preferences:', err);
             const msg = err.response && err.response.data && err.response.data.message ? err.response.data.message : 'Error saving preferences';
             this.$toast.error(msg);
          } finally {
             this.isLoading = false;
          }
       },
       async deleteCommPref(comm) {
          if (!confirm('Are you sure you want to delete this communication preference?')) return;
          
          this.isLoading = true;
          try {
             const orgUuid = this.profileData.uuid || this.profileData.id;
             const response = await this.$axios.delete(`/organisations/${orgUuid}/client-communications/${comm.id}`);
             if (response.data.success) {
                this.$toast.success(response.data.message);
                await this.fetchClientCommunications(orgUuid);
                await this.fetchCombinedTimeline(orgUuid);
                if (this.editingCommId === comm.id) {
                   this.cancelEditComm();
                }
             } else {
                this.$toast.error(response.data.message);
             }
          } catch (err) {
             console.error('Error deleting comm preference:', err);
             const msg = err.response && err.response.data && err.response.data.message ? err.response.data.message : 'Error deleting preference';
             this.$toast.error(msg);
          } finally {
             this.isLoading = false;
          }
       },
      isDirty(fieldKey) {
         if (!this.initialProfileData || !this.profileData) return false;
         if (Array.isArray(this.profileData[fieldKey])) {
            const a = this.profileData[fieldKey] || [];
            const b = this.initialProfileData[fieldKey] || [];
            if (a.length !== b.length) return true;
            return [...a].sort().join(',') !== [...b].sort().join(',');
         }
         return this.profileData[fieldKey] !== this.initialProfileData[fieldKey];
      },
      async saveField(fieldName, fieldKey) {
         const value = this.profileData[fieldKey];
         
         const classes = this.profileData.classifications || [];
         const classificationsPayload = classes.map(c => ({ classification: c }));

         // 1. If it is the leadName field, we update the Lead record
         if (fieldKey === 'leadName') {
            if (!this.profileData.leadUuid) {
               this.$toast.error('No contact lead associated with this organisation to update.');
               return;
            }
            const parts = value.trim().split(/\s+/);
            const firstName = parts[0] || 'Admin';
            const lastName = parts.slice(1).join(' ') || 'User';

            const leadPayload = {
               id: this.profileData.leadUuid,
               uuid: this.profileData.leadUuid,
               lead_type_id: this.profileData.leadTypeId,
               lead_status_id: this.profileData.leadStatusId || '801ee182-87c7-42d0-ad92-b87a9078a838',
               lead_priority_id: this.profileData.leadPriorityId || 'b73d7ad2-b0a5-49d3-ba51-e7ff0006133e',
               organisation_name: this.profileData.name,
               abn: this.profileData.abn,
               first_name: firstName,
               last_name: lastName,
               email: this.profileData.org_lead_email || this.profileData.email,
               phone_number: this.profileData.phone,
               is_charity: this.profileData.isCharity,
               country_code: this.profileData.country || 'Australia',
               classification: classes.join(', '),
               is_test_data: this.profileData.isTestData
            };

            try {
               await this.$axios.put(`/leads/${this.profileData.leadUuid}`, leadPayload);
               this.profileData.firstName = firstName;
               this.profileData.lastName = lastName;
               this.initialProfileData[fieldKey] = value;
               this.$toast.success(`${fieldName} saved successfully!`);
               await this.fetchCombinedTimeline(this.profileData.uuid);
            } catch (err) {
               console.error('Error saving lead name field:', err);
               this.$toast.error(`Failed to save ${fieldName}.`);
            }
            return;
         }

         // 2. For all other fields, we update the Organisation record
         const orgPayload = {
            id: this.profileData.uuid,
            uuid: this.profileData.uuid,
            name: this.profileData.name,
            abn: this.profileData.abn,
            email: this.profileData.email,
            org_lead_email: this.profileData.org_lead_email,
            phone_number: this.profileData.phone,
            address: this.profileData.address,
            address2: this.profileData.address2,
            city: this.profileData.city,
            state: this.profileData.state,
            country: this.profileData.country,
            postcode: this.profileData.postcode,

            billing_address: this.profileData.billing_address,
            billing_address2: this.profileData.billing_address2,
            billing_city: this.profileData.billing_city,
            billing_state: this.profileData.billing_state,
            billing_country: this.profileData.billing_country,
            billing_postcode: this.profileData.billing_postcode,
            billing_email: this.profileData.billing_email,

            delivery_address: this.profileData.delivery_address,
            delivery_address2: this.profileData.delivery_address2,
            delivery_city: this.profileData.delivery_city,
            delivery_state: this.profileData.delivery_state,
            delivery_country: this.profileData.delivery_country,
            delivery_postcode: this.profileData.delivery_postcode,

            service_address: this.profileData.service_address,
            service_address2: this.profileData.service_address2,
            service_city: this.profileData.service_city,
            service_state: this.profileData.service_state,
            service_country: this.profileData.service_country,
            service_postcode: this.profileData.service_postcode,
            service_email: this.profileData.service_email,

            background: this.profileData.background,
            is_charity: this.profileData.isCharity,
            is_test_data: this.profileData.isTestData,
            currency: this.profileData.currency,
            assigned_user_id: this.profileData.assigned_user_id ? parseInt(this.profileData.assigned_user_id) : 0,
            vertex_org_manager_id: this.profileData.vertex_org_manager_id ? parseInt(this.profileData.vertex_org_manager_id) : 0,
            is_msp_managed: this.profileData.isMspManaged,
            msp_uuid: this.profileData.mspUuid,
            is_partner_referred: this.profileData.isPartnerReferred,
            partner_uuid: this.profileData.partnerUuid,
            is_ecosystem: this.profileData.isEcosystem,
            ecosystem_uuid: this.profileData.ecosystemUuid,
            classifications: classificationsPayload
         };

         try {
            await this.$axios.put(`/organisations/${this.profileData.uuid}`, orgPayload);
            if (Array.isArray(value)) {
               this.initialProfileData[fieldKey] = [...value];
            } else {
               this.initialProfileData[fieldKey] = value;
            }
            this.$toast.success(`${fieldName} saved successfully!`);
            await this.fetchCombinedTimeline(this.profileData.uuid);
         } catch (err) {
            console.error('Error saving organisation field:', err);
            this.$toast.error(`Failed to save ${fieldName}.`);
         }
      },
      async saveClassifications() {
         const classes = this.profileData.classifications || [];
         const classificationsPayload = classes.map(c => ({ classification: c }));

         const orgPayload = {
            id: this.profileData.uuid,
            uuid: this.profileData.uuid,
            name: this.profileData.name,
            abn: this.profileData.abn,
            email: this.profileData.email,
            org_lead_email: this.profileData.org_lead_email,
            phone_number: this.profileData.phone,
            address: this.profileData.address,
            address2: this.profileData.address2,
            city: this.profileData.city,
            state: this.profileData.state,
            country: this.profileData.country,
            postcode: this.profileData.postcode,

            billing_address: this.profileData.billing_address,
            billing_address2: this.profileData.billing_address2,
            billing_city: this.profileData.billing_city,
            billing_state: this.profileData.billing_state,
            billing_country: this.profileData.billing_country,
            billing_postcode: this.profileData.billing_postcode,
            billing_email: this.profileData.billing_email,

            delivery_address: this.profileData.delivery_address,
            delivery_address2: this.profileData.delivery_address2,
            delivery_city: this.profileData.delivery_city,
            delivery_state: this.profileData.delivery_state,
            delivery_country: this.profileData.delivery_country,
            delivery_postcode: this.profileData.delivery_postcode,

            service_address: this.profileData.service_address,
            service_address2: this.profileData.service_address2,
            service_city: this.profileData.service_city,
            service_state: this.profileData.service_state,
            service_country: this.profileData.service_country,
            service_postcode: this.profileData.service_postcode,
            service_email: this.profileData.service_email,

            background: this.profileData.background,
            is_charity: this.profileData.isCharity,
            is_test_data: this.profileData.isTestData,
            currency: this.profileData.currency,
            assigned_user_id: this.profileData.assigned_user_id ? parseInt(this.profileData.assigned_user_id) : 0,
            vertex_org_manager_id: this.profileData.vertex_org_manager_id ? parseInt(this.profileData.vertex_org_manager_id) : 0,
            is_msp_managed: this.profileData.isMspManaged,
            msp_uuid: this.profileData.mspUuid,
            is_partner_referred: this.profileData.isPartnerReferred,
            partner_uuid: this.profileData.partnerUuid,
            is_ecosystem: this.profileData.isEcosystem,
            ecosystem_uuid: this.profileData.ecosystemUuid,
            classifications: classificationsPayload
         };

         try {
            await this.$axios.put(`/organisations/${this.profileData.uuid}`, orgPayload);
            this.initialProfileData.classifications = [...classes];
            this.initialProfileData.isTestData = this.profileData.isTestData;
            this.$toast.success('Classifications saved successfully!');
            await this.fetchCombinedTimeline(this.profileData.uuid);
         } catch (err) {
            console.error('Error saving classifications:', err);
            this.$toast.error('Failed to save Classifications.');
         }
      },
      formatDate(date) {
         if (!date) return '-';
         return moment(date).format('D-MMM-YYYY');
      },
      formatSyncedDate(date) {
         if (!date) return '';
         return moment(date).format('D-MMM-YYYY h:mm A');
      },
      debounceSave(fieldName, fieldKey) {
         const currentUuid = this.profileData.uuid;
         if (!currentUuid) return;

         this.fieldStates[fieldKey] = 'waiting';

         if (this.debounceTimeouts[fieldKey]) {
            clearTimeout(this.debounceTimeouts[fieldKey]);
         }

         this.debounceTimeouts[fieldKey] = setTimeout(async () => {
            if (this.profileData.uuid === currentUuid) {
               this.fieldStates[fieldKey] = 'saving';
               try {
                  await this.saveField(fieldName, fieldKey);
                  this.fieldStates[fieldKey] = 'saved';
                  setTimeout(() => {
                     if (this.fieldStates[fieldKey] === 'saved') {
                        this.fieldStates[fieldKey] = 'idle';
                     }
                  }, 2000);
               } catch (err) {
                  this.fieldStates[fieldKey] = 'idle';
               }
            } else {
               this.fieldStates[fieldKey] = 'idle';
            }
            delete this.debounceTimeouts[fieldKey];
         }, 3000);
      },
      async saveClassificationsImmediate() {
         this.fieldStates.classifications = 'saving';
         try {
            await this.saveClassifications();
            this.fieldStates.classifications = 'saved';
            setTimeout(() => {
               if (this.fieldStates.classifications === 'saved') {
                  this.fieldStates.classifications = 'idle';
               }
            }, 2000);
         } catch (err) {
            this.fieldStates.classifications = 'idle';
         }
      },
      async fetchMergedOrganisations(orgId) {
         const id = orgId || (this.profileData && (this.profileData.id || this.profileData.uuid));
         if (!id) return;
         this.isMergedOrgsLoading = true;
         try {
            const response = await this.$axios.get(`/organisations/${id}/merged-organisations`);
            if (response.data && response.data.merged_organisations) {
               this.mergedOrganisations = response.data.merged_organisations;
            } else {
               this.mergedOrganisations = [];
            }
         } catch (err) {
            console.error('Failed to fetch merged organisations:', err);
            this.mergedOrganisations = [];
         } finally {
            this.isMergedOrgsLoading = false;
         }
      },
      async openMergeModal() {
         this.selectedMergeTarget = null;
         this.mergeReason = '';
         this.mergeConfirmationChecked = false;
         this.showMergeModal = true;
         this.isSearchingMergeTargets = true;
         const currentUuid = this.profileData.uuid || this.profileData.id;
         const isSourceAuthSynced = this.profileData.has_auth_organisation == 1 || this.profileData.is_auth_synced || (this.profileData.auth_organisation_id && this.profileData.auth_organisation_id > 0);

         const filterEligibleTargets = (list) => {
            return list.filter(item => {
               const itemUuid = item.uuid || item.id;
               if (itemUuid === currentUuid || item.is_merged) return false;
               if (isSourceAuthSynced) {
                  const isItemAuth = item.has_auth_organisation == 1 || item.is_auth_synced || (item.auth_organisation_id && item.auth_organisation_id > 0);
                  if (!isItemAuth) return false;
               }
               return true;
            }).map(item => ({
               id: item.uuid || item.id,
               uuid: item.uuid || item.id,
               name: item.name || item.organisation_name || 'Unnamed Organisation',
               abn: item.abn || '',
               email: item.email || '',
               has_auth_organisation: item.has_auth_organisation,
               is_auth_synced: item.is_auth_synced,
               auth_organisation_id: item.auth_organisation_id
            }));
         };

         if (this.leads && this.leads.length > 0) {
            this.mergeSearchResults = filterEligibleTargets(this.leads);
         }

         try {
            const response = await this.$axios.get('/organisations?pageSize=10000&page=1&excludeTestData=false');
            if (response.data && response.data.organisations) {
               this.mergeSearchResults = filterEligibleTargets(response.data.organisations);
            }
         } catch (err) {
            console.error('Failed to load merge targets:', err);
         } finally {
            this.isSearchingMergeTargets = false;
         }
      },
      closeMergeModal() {
         this.showMergeModal = false;
         this.selectedMergeTarget = null;
         this.mergeSearchResults = [];
      },
      async confirmMerge() {
         if (!this.selectedMergeTarget || !this.mergeConfirmationChecked) return;
         const sourceUuid = this.profileData.uuid || this.profileData.id;
         const targetUuid = this.selectedMergeTarget.uuid || this.selectedMergeTarget.id;

         const isSourceAuthSynced = this.profileData.has_auth_organisation == 1 || this.profileData.is_auth_synced || (this.profileData.auth_organisation_id && this.profileData.auth_organisation_id > 0);
         const isTargetAuthSynced = this.selectedMergeTarget.has_auth_organisation == 1 || this.selectedMergeTarget.is_auth_synced || (this.selectedMergeTarget.auth_organisation_id && this.selectedMergeTarget.auth_organisation_id > 0);

         if (isSourceAuthSynced && !isTargetAuthSynced) {
            this.$toast.error('An Auth-synced organisation cannot be merged into an unlinked organisation. Destination must also be Auth-synced.');
            return;
         }

         this.isMerging = true;
         try {
            const response = await this.$axios.post(`/organisations/${sourceUuid}/merge-to`, {
               target_organisation_uuid: targetUuid,
               reason: this.mergeReason
            });
            if (response.data && (response.data.success || response.data.status === 'success')) {
               this.$toast.success(response.data.message || 'Organisation merged successfully');
               this.closeMergeModal();
               // Refresh list and switch view to the destination organisation
               await this.fetchLeads();
               this.selectedLeadId = targetUuid;
            } else {
               this.$toast.error((response.data && response.data.message) || 'Failed to merge organisation');
            }
         } catch (err) {
            console.error('Error merging organisation:', err);
            const msg = err.response && err.response.data && err.response.data.message ? err.response.data.message : 'Error merging organisation';
            this.$toast.error(msg);
         } finally {
            this.isMerging = false;
         }
      },
      openUnmergeModal(org) {
         this.unmergeTargetOrg = org || this.profileData;
         this.showUnmergeModal = true;
      },
      closeUnmergeModal() {
         this.showUnmergeModal = false;
         this.unmergeTargetOrg = null;
      },
      async confirmUnmerge() {
         if (!this.unmergeTargetOrg) return;
         const targetOrgUuid = this.unmergeTargetOrg.uuid || this.unmergeTargetOrg.id;
         this.isUnmerging = true;
         try {
            const response = await this.$axios.post(`/organisations/${targetOrgUuid}/unmerge`);
            if (response.data && (response.data.success || response.data.status === 'success')) {
               this.$toast.success(response.data.message || 'Organisation unmerged successfully');
               this.closeUnmergeModal();
               // Refresh the current organisation view
               const currentUuid = this.profileData.uuid || this.profileData.id;
               await this.fetchOrganisationDetails(currentUuid);
               await this.fetchLeads();
            } else {
               this.$toast.error((response.data && response.data.message) || 'Failed to unmerge organisation');
            }
         } catch (err) {
            console.error('Error unmerging organisation:', err);
            const msg = err.response && err.response.data && err.response.data.message ? err.response.data.message : 'Error unmerging organisation';
            this.$toast.error(msg);
         } finally {
            this.isUnmerging = false;
         }
      }
   }
};
</script>

<style scoped>
.client-dashboard-page {
   padding: 10px 0;
}

.breadcrumb-link {
   transition: color 0.2s ease;
}
.breadcrumb-link:hover {
   color: #00A0C1 !important;
}

.back-to-all-btn {
   transition: all 0.2s ease;
}
.back-to-all-btn:hover {
   background-color: #00A0C1 !important;
   color: #fff !important;
   border-color: #00A0C1 !important;
   box-shadow: 0 4px 12px rgba(0, 160, 193, 0.25);
}

/* Page Header */
.ms-page-header {
   margin-bottom: 24px;
   padding: 20px 24px;
   background: linear-gradient(135deg, #ffffff 0%, #f4f9fb 100%);
   border: 1px solid rgba(0, 130, 152, 0.15);
   border-left: 5px solid #008298;
   border-radius: 12px;
   box-shadow: 0 4px 18px rgba(0, 78, 90, 0.05);
}

.ms-page-header__content {
   display: flex;
   align-items: center;
   gap: 16px;
}

.ms-page-header__icon {
   width: 44px;
   height: 44px;
   border-radius: 10px;
   background: rgba(0, 130, 152, 0.08);
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 1.15rem;
   color: #008298;
}

.ms-page-header__title {
   font-size: 1.45rem;
   font-weight: 800;
   color: #00363f;
   margin: 0;
   letter-spacing: -0.3px;
}

.org-switcher-box,
.org-switcher-section {
   display: flex;
   flex-direction: column;
   align-items: flex-start;
}

.org-switcher-title,
.switcher-label {
   font-size: 0.72rem;
   color: #557d84;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   margin-bottom: 4px;
}

.header-search-container {
   position: relative;
   width: 320px;
   margin: 8px 0;
}

.header-search-input-wrapper {
   position: relative;
   display: flex;
   align-items: center;
}

.header-search-input-wrapper .search-icon {
   position: absolute;
   left: 12px;
   color: #94a3b8;
   font-size: 0.9rem;
   pointer-events: none;
}

.header-search-input {
   width: 100%;
   padding: 8px 36px 8px 36px;
   font-size: 0.85rem;
   border: 1.5px solid #cbd5e1;
   border-radius: 8px;
   color: #334155;
   background-color: #fff;
   transition: all 0.2s ease;
   height: 38px;
   outline: none;
}

.header-search-input:focus {
   border-color: #00A0C1;
   box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
}

.clear-search-btn {
   position: absolute;
   right: 12px;
   background: none;
   border: none;
   color: #94a3b8;
   cursor: pointer;
   padding: 4px;
   display: flex;
   align-items: center;
   justify-content: center;
   border-radius: 50%;
   transition: all 0.2s ease;
}

.clear-search-btn:hover {
   color: #64748b;
   background-color: #f1f5f9;
}

/* Dropdown overlay */
.header-search-dropdown {
   position: absolute;
   top: 100%;
   left: 0;
   right: 0;
   background-color: #fff;
   border: 1.5px solid #e2e8f0;
   border-radius: 8px;
   box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
   z-index: 1000;
   max-height: 250px;
   overflow-y: auto;
   margin-top: 6px;
}

.header-search-dropdown-item {
   padding: 10px 14px;
   cursor: pointer;
   transition: background-color 0.15s ease;
}

.header-search-dropdown-item:hover {
   background-color: #f1f5f9;
}

.org-name-text {
   font-weight: 600;
   color: #1e293b;
   font-size: 0.85rem;
}

.org-type-badge {
   font-size: 0.7rem;
   font-weight: 700;
   padding: 3px 8px;
   border-radius: 6px;
   background-color: #f8fafc;
   color: #64748b;
   border: 1px solid #e2e8f0;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   display: inline-flex;
   align-items: center;
   white-space: nowrap;
}

.org-type-badge--lead {
   background-color: #dcfce7;
   color: #15803d;
   border-color: #bbf7d0;
}

.org-type-badge--client,
.org-type-badge--organisation {
   background-color: #ffedd5;
   color: #c2410c;
   border-color: #fed7aa;
}

.org-type-badge--prospect {
   background-color: #fef3c7;
   color: #b45309;
   border-color: #fde68a;
}

.org-type-badge--partner {
   background-color: #ede9fe;
   color: #6d28d9;
   border-color: #ddd6fe;
}

.org-type-badge--supplier {
   background-color: #e0f2fe;
   color: #0369a1;
   border-color: #bae6fd;
}

.org-type-badge--merged {
   background-color: #fffbeb;
   color: #92400e;
   border-color: #fde68a;
}

.org-type-badge--deactivated {
   background-color: #fee2e2;
   color: #dc2626;
   border-color: #fecaca;
}

.org-switcher-multiselect {
   min-width: 260px;
   max-width: 380px;
   cursor: pointer;
}

/* VueMultiselect styles moved to unscoped block at bottom of file */

/* Organisation Header Card */
.org-header-card {
   background: #ffffff;
   border-radius: 12px !important;
   border: 1px solid #e2e8f0 !important;
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05) !important;
}

.org-avatar-box {
   width: 48px;
   height: 48px;
   border-radius: 10px;
   background: #e0f2fe;
   color: #0284c7;
   font-size: 1.4rem;
   flex-shrink: 0;
}

.org-header-title {
   font-size: 1.35rem;
   font-weight: 700;
   color: #0f172a;
   letter-spacing: -0.01em;
}

.org-header-subtitle {
   color: #64748b;
   font-size: 0.82rem;
   font-weight: 500;
}

.org-pill-badge {
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 4px 10px;
   border-radius: 14px;
   font-size: 0.8125rem;
   font-weight: 600;
   line-height: 1.35;
   letter-spacing: 0.2px;
}

.org-pill-badge--lead {
   background-color: #dcfce7;
   color: #15803d;
}

.org-pill-badge--client,
.org-pill-badge--organisation {
   background-color: #ffedd5;
   color: #c2410c;
}

.org-pill-badge--prospect {
   background-color: #fef3c7;
   color: #b45309;
}

.org-pill-badge--supplier {
   background-color: #e0f2fe;
   color: #0369a1;
}

.org-pill-badge--partner {
   background-color: #ede9fe;
   color: #6d28d9;
}

.org-pill-badge--owner {
   background-color: #f1f5f9;
   color: #334155;
   border: 1px solid #cbd5e1;
}

.org-pill-badge--test {
   background-color: #fee2e2;
   color: #b91c1c;
}

.org-pill-badge--synced {
   background-color: #ecfdf5;
   color: #059669;
   border: 1px solid #a7f3d0;
}

.org-pill-badge--linked {
   background-color: #eef2ff;
   color: #4f46e5;
   border: 1px solid #c7d2fe;
}

.org-pill-badge--marked {
   background-color: #eff6ff;
   color: #2563eb;
   border: 1px solid #bfdbfe;
}

.org-pill-badge--merged {
   background-color: #fffbeb;
   color: #92400e;
   border: 1px solid #fde68a;
}

.org-pill-badge--merged i {
   color: #d97706;
}

.org-switcher-box {
   display: flex;
   flex-direction: column;
   align-items: flex-end;
}

.org-switcher-title {
   font-size: 0.72rem;
   font-weight: 600;
   color: #64748b;
   margin-bottom: 4px;
}

/* Cards (Matching All Organisations) */
.ms-card {
   background: #fff;
   border-radius: 12px;
   border: 1px solid #e2e8f0;
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
   overflow: hidden;
}

/* Premium Tab Navigation (Matching All Organisations) */
.org-tabs-container {
   background-color: #f8fafc;
   border-bottom: 1.5px solid #e2e8f0;
   padding: 0 24px;
   display: flex;
   justify-content: space-between;
   align-items: center;
   flex-wrap: wrap;
   gap: 16px;
}

.org-tabs-nav {
   display: flex;
   gap: 4px;
   flex-wrap: wrap;
}

.org-tab-btn {
   background: none;
   border: none;
   padding: 16px 20px;
   font-weight: 600;
   color: #64748b;
   cursor: pointer;
   position: relative;
   transition: all 0.2s ease;
   font-size: 0.9rem;
   outline: none;
   display: inline-flex;
   align-items: center;
   text-decoration: none;
   user-select: none;
}

.org-tab-btn:hover {
   color: #00768a;
}

.org-tab-btn.active {
   color: #00768a;
   font-weight: 700;
}

.org-tab-btn.active::after {
   content: '';
   position: absolute;
   bottom: 0;
   left: 0;
   right: 0;
   height: 3px;
   background-color: #00768a;
   border-radius: 3px 3px 0 0;
}

.org-tab-counter-badge {
   display: inline-flex;
   align-items: center;
   justify-content: center;
   min-width: 20px;
   height: 20px;
   padding: 0 6px;
   border-radius: 10px;
   font-size: 0.72rem;
   font-weight: 700;
   line-height: 1;
   margin-left: 6px;
   background-color: #e2e8f0;
   color: #475569;
   transition: all 0.2s ease;
}

.org-tab-btn.active .org-tab-counter-badge {
   background-color: #00768a;
   color: #ffffff;
}

/* Accordion Section Cards */
.accordion-card {
   background: #ffffff;
   border-radius: 12px !important;
   border: 1px solid #e2e8f0 !important;
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
   overflow: hidden;
}

.accordion-header {
   cursor: pointer;
   user-select: none;
   background: #ffffff;
   transition: background-color 0.15s ease;
}

.accordion-header:hover {
   background-color: #fafbfd;
}

.accordion-title {
   font-size: 1rem;
   font-weight: 700;
   color: #1e293b;
}

.btn-accordion-chevron {
   width: 28px;
   height: 28px;
   border-radius: 6px;
   border: 1px solid #e2e8f0;
   background: #ffffff;
   color: #64748b;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 0.75rem;
   transition: all 0.2s ease;
}

.btn-accordion-chevron:hover {
   background-color: #f1f5f9;
   color: #1e293b;
}

/* Status Badge in Header */
.status-pill {
   padding: 4px 10px;
   border-radius: 6px;
   font-size: 0.75rem;
   font-weight: 600;
   cursor: default;
   user-select: none;
   display: inline-flex;
   align-items: center;
   letter-spacing: 0.2px;
}

.status-pill--not-linked {
   background-color: #fff7ed !important;
   color: #c2410c !important;
   border: 1px solid #fed7aa !important;
}

.status-pill--linked {
   background-color: #dcfce7 !important;
   color: #15803d !important;
   border: 1px solid #86efac !important;
}

.status-pill--marked {
   background-color: #eff6ff !important;
   color: #1d4ed8 !important;
   border: 1px solid #bfdbfe !important;
}

/* Platform Action Icons in Accordion Header */
.btn-accordion-icon {
   width: 30px;
   height: 30px;
   border-radius: 6px;
   border: 1px solid #e2e8f0;
   background: #ffffff;
   color: #94a3b8;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 0.8rem;
   transition: all 0.2s ease;
   cursor: pointer;
}

.btn-accordion-icon.is-linked {
   color: #00A0C1;
   border-color: #cbd5e1;
}

.btn-accordion-icon.btn-acc-xero.is-linked {
   color: #16a34a;
}

.btn-accordion-icon.btn-acc-sync.is-linked {
   color: #0d9488;
}

.btn-accordion-icon:hover:not(:disabled) {
   transform: translateY(-1px);
   box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.btn-accordion-icon.btn-acc-auth.is-linked:hover {
   background-color: #00A0C1;
   color: #ffffff;
   border-color: #00A0C1;
}

.btn-accordion-icon.btn-acc-xero.is-linked:hover {
   background-color: #16a34a;
   color: #ffffff;
   border-color: #16a34a;
}

.btn-accordion-icon.btn-acc-sync.is-linked:hover:not(:disabled) {
   background-color: #0d9488;
   color: #ffffff;
   border-color: #0d9488;
}

.btn-accordion-icon:disabled {
   opacity: 0.45;
   cursor: not-allowed;
   pointer-events: auto;
}

.btn-accordion-icon.is-syncing {
   cursor: wait;
   color: #0d9488;
   background-color: #f0fdfa;
   border-color: #99f6e4;
}

/* Relationship Header Badges */
.rel-header-badge {
   padding: 4px 10px;
   border-radius: 6px;
   font-size: 0.72rem;
   font-weight: 600;
   display: inline-flex;
   align-items: center;
}

.rel-badge-msp {
   background-color: #eff6ff;
   color: #1d4ed8;
   border: 1px solid #bfdbfe;
}

.rel-badge-partner {
   background-color: #f5f3ff;
   color: #6d28d9;
   border: 1px solid #ddd6fe;
}

.rel-badge-ecosystem {
   background-color: #f0fdfa;
   color: #0f766e;
   border: 1px solid #99f6e4;
}

/* Forms */
.form-section-title {
   font-size: 1.1rem;
   font-weight: 700;
   color: #334155;
   margin-bottom: 20px;
   padding-bottom: 8px;
   border-bottom: 1px dashed #e2e8f0;
}

.form-label {
   font-weight: 600;
   color: #475569;
   font-size: 0.85rem;
   margin-bottom: 6px;
}

.form-control, .form-select {
   border-radius: 10px;
   border: 1.5px solid #cbd5e1;
   padding: 10px 14px;
   font-size: 0.9rem;
   color: #334155;
   transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus, .form-select:focus {
   border-color: #00A0C1;
   box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
}

.relation-list {
   background: #f8fafc;
   border: 1.5px solid #e2e8f0;
   border-radius: 10px;
   padding: 10px;
   display: flex;
   flex-direction: column;
   gap: 6px;
}

.relation-item {
   padding: 8px 12px;
   background: #fff;
   border-radius: 8px;
   border: 1px solid #e2e8f0;
   font-size: 0.85rem;
   font-weight: 500;
}

.partner-tag {
   display: inline-flex;
   align-items: center;
   background-color: #f1f5f9;
   color: #334155;
   padding: 6px 12px;
   border-radius: 8px;
   font-weight: 500;
   font-size: 0.85rem;
   border: 1px solid #cbd5e1;
}

.partner-tag i:hover {
   color: #dc2626;
}

.btn-save {
   border-radius: 10px !important;
   height: 40px;
}


/* Vertical Timeline */
.vertical-timeline {
   position: relative;
   padding-left: 30px;
}

.vertical-timeline::before {
   content: '';
   position: absolute;
   top: 0;
   bottom: 0;
   left: 9px;
   width: 2px;
   background-color: #e2e8f0;
}

.timeline-event {
   position: relative;
   margin-bottom: 24px;
}

.timeline-event:last-child {
   margin-bottom: 0;
}

.timeline-icon {
   position: absolute;
   left: -33px;
   width: 26px;
   height: 26px;
   background-color: #fff;
   border: 2px solid #cbd5e1;
   border-radius: 50%;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 0.75rem;
   z-index: 1;
}

.timeline-body {
   background: #f8fafc;
   border: 1px solid #e2e8f0;
   border-radius: 10px;
   padding: 14px 16px;
}

.event-title {
   font-size: 0.9rem;
}

.event-description {
   font-size: 0.85rem;
}

/* Individual Save Buttons - Contextual Overlay Design */
.field-save-wrapper {
   position: relative;
   display: flex;
   align-items: center;
   width: 100%;
}

.field-save-wrapper .form-control {
   padding-right: 48px !important;
}

/* Field Status Indicators */
.field-status-indicator {
   position: absolute;
   right: 12px;
   display: flex;
   align-items: center;
   justify-content: center;
   height: 100%;
   z-index: 5;
   pointer-events: none;
}

.field-status-indicator--textarea {
   bottom: 22px;
   right: 12px;
   height: auto;
}

.field-status-indicator-static {
   display: inline-flex;
   align-items: center;
   justify-content: center;
   height: 28px;
   width: 28px;
}

.status-text {
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 0.95rem;
   animation: fadeIn 0.3s ease;
}

.status-text.waiting {
   font-size: 0.85rem;
}

.status-text.saving {
   font-size: 0.85rem;
}

.status-text.saved {
   font-size: 1.05rem;
}

/* ── Linked status badges ── */
.lead-linked-badge {
   display: inline-flex;
   align-items: center;
   gap: 5px;
   padding: 4px 10px;
   background: #ecfdf5;
   color: #059669;
   border-radius: 16px;
   font-size: 0.78rem;
   font-weight: 600;
   white-space: nowrap;
}

.lead-linked-badge i {
   font-size: 0.7rem;
}

.lead-unlinked-badge {
   display: inline-flex;
   align-items: center;
   gap: 5px;
   padding: 4px 10px;
   background: #fef2f2;
   color: #dc2626;
   border-radius: 16px;
   font-size: 0.78rem;
   font-weight: 600;
   white-space: nowrap;
}

.lead-unlinked-badge i {
   font-size: 0.7rem;
}

/* Modal Overlay & Card custom styles for link modal */
.lead-modal-overlay {
   position: fixed;
   top: 0;
   left: 0;
   right: 0;
   bottom: 0;
   background-color: rgba(15, 23, 42, 0.4);
   display: flex;
   align-items: center;
   justify-content: center;
   z-index: 1050;
   backdrop-filter: blur(4px);
}

.lead-modal {
   width: 90%;
   max-width: 500px;
   background: #fff;
   border-radius: 16px;
   box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
   overflow: hidden;
}

.lead-modal__header {
   padding: 16px 20px;
   display: flex;
   justify-content: space-between;
   align-items: center;
   background: #ffffff;
}

.lead-modal__title {
   margin: 0;
   font-size: 1.1rem;
   font-weight: 600;
   color: #1e293b;
   display: flex;
   align-items: center;
   gap: 8px;
}

.lead-modal__close {
   background: transparent;
   border: none;
   font-size: 1.5rem;
   color: #64748b;
   cursor: pointer;
   line-height: 1;
   padding: 0;
   transition: color 0.15s ease;
}

.lead-modal__close:hover {
   color: #ef4444;
}

.lead-modal__divider {
   height: 1px;
   background: #e2e8f0;
}

.lead-modal__body {
   padding: 20px;
}

.lead-form-label {
   display: block;
   font-weight: 500;
   font-size: 0.875rem;
   color: #475569;
   margin-bottom: 8px;
}

.lead-modal__actions {
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   margin-top: 24px;
}

/* Premium Styled Checkbox Custom CSS */
.checkbox-label {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  font-weight: 500;
  color: #333 !important;
}

.styled-checkbox {
  display: none;
}

.checkbox-custom {
  width: 20px;
  height: 20px;
  border: 2px solid #00bcd4;
  border-radius: 4px;
  margin-right: 8px;
  display: inline-block;
  position: relative;
  transition: background-color 0.2s, border-color 0.2s;
}

.checkbox-custom::after {
  content: '';
  position: absolute;
  display: none;
  left: 6px;
  top: 2px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 3px 3px 0;
  transform: rotate(45deg);
}

.styled-checkbox:checked+.checkbox-custom {
  background-color: #00bcd4;
  border-color: #00bcd4;
}

.styled-checkbox:checked+.checkbox-custom::after {
  display: block;
}

/* Premium Custom Toggle Switches */
.switch-toggle {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}

.switch-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.switch-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .3s;
  border-radius: 24px;
}

.switch-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .3s;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
}

input:checked + .switch-slider {
  background-color: #00bcd4;
}

input:checked + .switch-slider:before {
  transform: translateX(20px);
}

/* Custom Styles for Client Communication Cards and Badges */
.comm-preference-card {
   background-color: #ffffff;
   border: 1px solid #e2e8f0 !important;
   border-radius: 12px;
   box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03) !important;
   transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.comm-preference-card:hover {
   transform: translateY(-4px);
   box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04) !important;
}
.comm-badge {
   padding: 6px 12px;
   border-radius: 20px;
   font-size: 0.72rem;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   gap: 6px;
   box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
   transition: all 0.2s ease;
   cursor: help;
}
.comm-badge:hover {
   transform: translateY(-1px);
   box-shadow: 0 3px 6px rgba(0, 0, 0, 0.08);
}
/* Uniform Intensity Badges for Client Communication Module */
.comm-badge--intensity-low {
   background-color: #f0fdf4 !important;
   color: #166534 !important;
   border: 1px solid #bbf7d0 !important;
}
.comm-badge--intensity-medium {
   background-color: #eff6ff !important;
   color: #1e40af !important;
   border: 1px solid #bfdbfe !important;
}
.comm-badge--intensity-high {
   background-color: #fffbeb !important;
   color: #9a3412 !important;
   border: 1px solid #fde68a !important;
}
.comm-badge--intensity-critical {
   background-color: #fef2f2 !important;
   color: #991b1b !important;
   border: 1px solid #fecaca !important;
}
.btn-icon-only {
   width: 32px;
   height: 32px;
   border-radius: 50% !important;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   padding: 0 !important;
   transition: all 0.2s ease;
}
.comm-card-actions .btn-outline-secondary {
   border-color: #cbd5e1;
   color: #64748b;
}
.comm-card-actions .btn-outline-secondary:hover {
   background-color: #f1f5f9;
   color: #334155;
   border-color: #94a3b8;
}
.comm-card-actions .btn-outline-danger {
   border-color: #fca5a5;
   color: #ef4444;
}
.comm-card-actions .btn-outline-danger:hover {
   background-color: #fef2f2;
   color: #dc2626;
   border-color: #f87171;
}

/* Custom BDM-style Modal Styles */
.modal-backdrop-custom {
   position: fixed;
   top: 0;
   left: 0;
   width: 100vw;
   height: 100vh;
   background-color: rgba(15, 23, 42, 0.4);
   display: flex;
   justify-content: center;
   align-items: center;
   z-index: 1050;
   backdrop-filter: blur(4px);
}
.modal-content-custom {
   position: relative;
   background-color: white;
   padding: 2.5rem 2rem 2rem 2rem;
   border-radius: 16px;
   width: 100%;
   box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}
.modal-title-custom {
   font-weight: bold;
   font-size: 1.25rem;
   text-align: left;
   color: #435666;
   margin-bottom: 0;
}
.modal-content-custom hr {
   margin-top: 1rem;
   margin-bottom: 1.5rem;
   border: 0;
   border-top: 1px solid #e2e8f0;
   opacity: 1;
}
.close-button-custom {
   position: absolute;
   top: 1rem;
   right: 1rem;
   font-size: 2rem;
   font-weight: bold;
   line-height: 1;
   color: #888;
   background: none;
   border: none;
   cursor: pointer;
   transition: color 0.2s ease;
}
.close-button-custom:hover {
   color: #000;
}
.add-button-custom {
   font-size: 1rem;
   font-weight: bold;
   border-radius: 8px;
}
</style>

<style>
/* Unscoped overrides for Organisation Switcher VueMultiselect */
.org-switcher-multiselect .multiselect__tags {
   background: #ffffff !important;
   border: 1.5px solid rgba(0, 130, 152, 0.15) !important;
   border-radius: 10px !important;
   height: 38px !important;
   min-height: 38px !important;
   padding: 6px 40px 0 12px !important;
}

.org-switcher-multiselect .multiselect__single {
   background: transparent !important;
   color: #00363f !important;
   font-size: 0.9rem !important;
   font-weight: 600 !important;
   margin-bottom: 0 !important;
   padding-left: 0 !important;
}

.org-switcher-multiselect .multiselect__input {
   background: transparent !important;
   color: #00363f !important;
   font-size: 0.9rem !important;
   font-weight: 600 !important;
   margin-bottom: 0 !important;
   padding-left: 0 !important;
}

.org-switcher-multiselect .multiselect__input::placeholder {
   color: #94a3b8 !important;
}

.org-switcher-multiselect .multiselect__select {
   height: 36px !important;
   width: 38px !important;
}

.org-switcher-multiselect .multiselect__select::before {
   border-color: #008298 transparent transparent !important;
}

.org-switcher-multiselect .multiselect__placeholder {
   color: #94a3b8 !important;
   font-size: 0.9rem !important;
   font-weight: 600 !important;
   margin-bottom: 0 !important;
   padding-left: 0 !important;
}

.org-switcher-multiselect .multiselect__content-wrapper {
   border-radius: 10px !important;
   border: 1px solid rgba(0, 130, 152, 0.15) !important;
   box-shadow: 0 4px 12px rgba(0, 78, 90, 0.08) !important;
   background: #ffffff !important;
}

.org-switcher-multiselect .multiselect__option {
   background: #ffffff !important;
   color: #1e293b !important;
   font-weight: 500 !important;
}

.org-switcher-multiselect .multiselect__option--highlight {
   background: #008298 !important;
   color: white !important;
}

.org-switcher-multiselect .multiselect__option--selected {
   background: #e6f4f6 !important;
   color: #008298 !important;
   font-weight: 700 !important;
}

.org-switcher-multiselect.multiselect {
   background-color: transparent !important;
   background-image: none !important;
}

/* Merge Target VueMultiselect Styling */
.merge-target-multiselect .multiselect__option {
   background: #ffffff !important;
   color: #1e293b !important;
   padding: 8px 14px !important;
   min-height: auto !important;
}

.merge-target-multiselect .multiselect__option::after {
   display: none !important;
}

.merge-target-multiselect .merge-option-name {
   color: #1e293b !important;
   font-weight: 600;
   transition: color 0.15s ease;
}

.merge-target-multiselect .merge-option-sub {
   color: #64748b !important;
   transition: color 0.15s ease;
}

.merge-target-multiselect .merge-auth-badge {
   transition: all 0.15s ease;
}

/* Highlighted / Hovered / Keyboard Active Option */
.merge-target-multiselect .multiselect__option--highlight {
   background: #00A0C1 !important;
   color: #ffffff !important;
}

.merge-target-multiselect .multiselect__option--highlight::after {
   background: #00A0C1 !important;
   color: #ffffff !important;
}

.merge-target-multiselect .multiselect__option--highlight *,
.merge-target-multiselect .multiselect__option--highlight .merge-option-name {
   color: #ffffff !important;
}

.merge-target-multiselect .multiselect__option--highlight .merge-option-sub {
   color: rgba(255, 255, 255, 0.92) !important;
}

.merge-target-multiselect .multiselect__option--highlight .merge-auth-badge,
.merge-target-multiselect .multiselect__option--highlight .badge {
   background-color: rgba(255, 255, 255, 0.25) !important;
   color: #ffffff !important;
   border-color: rgba(255, 255, 255, 0.5) !important;
}

.merge-target-multiselect .multiselect__option--highlight .merge-auth-badge i,
.merge-target-multiselect .multiselect__option--highlight .badge i {
   color: #ffffff !important;
}

/* Selected Option (Normal state) */
.merge-target-multiselect .multiselect__option--selected {
   background: #e6f4f6 !important;
   color: #00768a !important;
   font-weight: 700 !important;
}

.merge-target-multiselect .multiselect__option--selected::after {
   background: #e6f4f6 !important;
   color: #00768a !important;
}

.merge-target-multiselect .multiselect__option--selected .merge-option-name {
   color: #00768a !important;
}

.merge-target-multiselect .multiselect__option--selected .merge-option-sub {
   color: #00768a !important;
}

.merge-target-multiselect .multiselect__option--selected .merge-auth-badge,
.merge-target-multiselect .multiselect__option--selected .badge {
   background-color: rgba(0, 118, 138, 0.12) !important;
   color: #00768a !important;
   border-color: rgba(0, 118, 138, 0.25) !important;
}

/* Selected AND Highlighted Option */
.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight {
   background: #008fae !important;
   color: #ffffff !important;
}

.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight::after {
   background: #008fae !important;
   color: #ffffff !important;
}

.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight *,
.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight .merge-option-name {
   color: #ffffff !important;
}

.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight .merge-option-sub {
   color: rgba(255, 255, 255, 0.92) !important;
}

.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight .merge-auth-badge,
.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight .badge {
   background-color: rgba(255, 255, 255, 0.25) !important;
   color: #ffffff !important;
   border-color: rgba(255, 255, 255, 0.5) !important;
}

.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight .merge-auth-badge i,
.merge-target-multiselect .multiselect__option--selected.multiselect__option--highlight .badge i {
   color: #ffffff !important;
}

/* Platform Action Buttons styling */
.platform-actions-toolbar {
   background: #f8fafc;
   border: 1px solid #e2e8f0;
   border-radius: 8px;
   padding: 3px;
   display: inline-flex;
   align-items: center;
}

.btn-platform-icon {
   width: 34px;
   height: 34px;
   padding: 0 !important;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   border-radius: 6px !important;
   font-size: 0.875rem;
   border: 1px solid transparent;
   background-color: transparent;
   color: #94a3b8;
   cursor: not-allowed;
   transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-platform-icon:disabled {
   opacity: 0.45;
   cursor: not-allowed;
}

/* Auth Action Icon Button */
.btn-platform-auth.is-linked {
   color: #00A0C1;
   background-color: #ffffff;
   border-color: #cbd5e1;
   cursor: pointer;
}
.btn-platform-auth.is-linked:hover {
   background-color: #00A0C1 !important;
   border-color: #00A0C1 !important;
   color: #ffffff !important;
   transform: translateY(-1px);
   box-shadow: 0 2px 5px rgba(0, 160, 193, 0.25);
}

/* Xero Action Icon Button */
.btn-platform-xero.is-linked {
   color: #16a34a;
   background-color: #ffffff;
   border-color: #cbd5e1;
   cursor: pointer;
}
.btn-platform-xero.is-linked:hover {
   background-color: #16a34a !important;
   border-color: #16a34a !important;
   color: #ffffff !important;
   transform: translateY(-1px);
   box-shadow: 0 2px 5px rgba(22, 163, 74, 0.25);
}

/* Sync Action Icon Button */
.btn-platform-sync.is-linked {
   color: #0d9488;
   background-color: #ffffff;
   border-color: #cbd5e1;
   cursor: pointer;
}
.btn-platform-sync.is-linked:hover:not(:disabled) {
   background-color: #0d9488 !important;
   border-color: #0d9488 !important;
   color: #ffffff !important;
   transform: translateY(-1px);
   box-shadow: 0 2px 5px rgba(13, 148, 136, 0.25);
}
.btn-platform-sync.is-syncing {
   color: #0d9488;
   background-color: #f0fdfa;
   border-color: #99f6e4;
   cursor: wait;
}

.lead-former-badge {
   display: inline-flex;
   align-items: center;
   gap: 5px;
   padding: 2px 8px;
   background: #fef3c7;
   color: #92400e;
   border: 1px solid #fde68a;
   border-radius: 12px;
   font-size: 0.72rem;
   font-weight: 600;
   white-space: nowrap;
}

.lead-former-badge i {
   font-size: 0.68rem;
   color: #b45309;
}

/* ========================================================
   Contacts Tab & Key Stakeholders Styling
   ======================================================== */


/* Contact Avatars */
.contact-mini-avatar {
   width: 32px;
   height: 32px;
   border-radius: 50%;
   background: #e2e8f0;
   color: #334155;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 11.5px;
   font-weight: 700;
   flex-shrink: 0;
   text-transform: uppercase;
}

.contact-card-avatar {
   width: 44px;
   height: 44px;
   border-radius: 50%;
   background: #e2e8f0;
   color: #334155;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 15px;
   font-weight: 700;
   flex-shrink: 0;
   text-transform: uppercase;
}

.avatar-primary {
   background: linear-gradient(135deg, #fef3c7, #fde68a);
   color: #92400e;
   box-shadow: 0 2px 4px rgba(245, 158, 11, 0.15);
}

.avatar-technical {
   background: linear-gradient(135deg, #e0f2fe, #bae6fd);
   color: #0369a1;
   box-shadow: 0 2px 4px rgba(2, 132, 199, 0.15);
}

.avatar-billing {
   background: linear-gradient(135deg, #dcfce7, #bbf7d0);
   color: #15803d;
   box-shadow: 0 2px 4px rgba(34, 197, 94, 0.15);
}

.avatar-signer {
   background: linear-gradient(135deg, #f3e8ff, #e9d5ff);
   color: #7e22ce;
   box-shadow: 0 2px 4px rgba(168, 85, 247, 0.15);
}

.avatar-user {
   background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
   color: #475569;
}

.avatar-lead {
   background: linear-gradient(135deg, #ffedd5, #fed7aa);
   color: #c2410c;
}

.avatar-default {
   background: #f1f5f9;
   color: #64748b;
}

/* Contact Badges (aligned with VertexProposal) */
.contact-badge-item {
   letter-spacing: 0.3px;
   box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.contact-badge-item + .contact-badge-item {
   margin-left: 6px !important;
}

.contact-type-badge-primary {
   background-color: #fef3c7 !important;
   color: #92400e !important;
   border: 1px solid #fde68a !important;
}

.contact-type-badge-technical {
   background-color: #e0f2fe !important;
   color: #0369a1 !important;
   border: 1px solid #bae6fd !important;
}

.contact-type-badge-billing {
   background-color: #dcfce7 !important;
   color: #15803d !important;
   border: 1px solid #bbf7d0 !important;
}

.contact-type-badge-signer {
   background-color: #f3e8ff !important;
   color: #7e22ce !important;
   border: 1px solid #e9d5ff !important;
}

.contact-type-badge-user {
   background-color: #f1f5f9 !important;
   color: #475569 !important;
   border: 1px solid #cbd5e1 !important;
}

.contact-type-badge-lead {
   background-color: #ffedd5 !important;
   color: #c2410c !important;
   border: 1px solid #fed7aa !important;
}

.contact-type-badge-default {
   background-color: #f1f5f9 !important;
   color: #64748b !important;
   border: 1px solid #e2e8f0 !important;
}

/* Cards & Mini-cards */
.contact-preview-mini-card {
   transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}
.contact-preview-mini-card:hover {
   transform: translateY(-2px);
   box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
   border-color: #cbd5e1 !important;
}

.contact-directory-card {
   border-radius: 12px !important;
   border: 1px solid #f1f5f9 !important;
   background: #ffffff;
   transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}
.contact-directory-card:hover {
   transform: translateY(-3px);
   box-shadow: 0 8px 24px rgba(0, 0, 0, 0.07) !important;
   border-color: #cbd5e1 !important;
}



/* Copy email button */
.copy-email-btn {
   opacity: 0.6;
   transition: opacity 0.15s ease, transform 0.15s ease;
}
.copy-email-btn:hover {
   opacity: 1;
   transform: scale(1.15);
}

/* Vertex Theme Buttons */
.btn-vertex-primary {
   background-color: #00A0C1 !important;
   border: 1px solid #00A0C1 !important;
   color: #ffffff !important;
   font-weight: 600 !important;
   border-radius: 6px !important;
   transition: all 0.2s ease-in-out !important;
}

.btn-vertex-primary:hover,
.btn-vertex-primary:focus {
   background-color: #008298 !important;
   border-color: #008298 !important;
   color: #ffffff !important;
   box-shadow: 0 2px 8px rgba(0, 160, 193, 0.35) !important;
   transform: translateY(-1px);
}

.btn-vertex-primary:disabled {
   background-color: #a0d8e6 !important;
   border-color: #a0d8e6 !important;
   color: #ffffff !important;
   opacity: 0.65 !important;
   transform: none !important;
   box-shadow: none !important;
   cursor: not-allowed !important;
}

.btn-vertex-outline {
   background-color: #ffffff !important;
   border: 1px solid #00A0C1 !important;
   color: #00A0C1 !important;
   font-weight: 600 !important;
   border-radius: 6px !important;
   transition: all 0.2s ease-in-out !important;
}

.btn-vertex-outline:hover,
.btn-vertex-outline:focus {
   background-color: #00A0C1 !important;
   border-color: #00A0C1 !important;
   color: #ffffff !important;
   box-shadow: 0 2px 6px rgba(0, 160, 193, 0.2) !important;
   transform: translateY(-1px);
}

.btn-vertex-outline:disabled {
   border-color: #cbd5e1 !important;
   color: #94a3b8 !important;
   opacity: 0.65 !important;
   transform: none !important;
   box-shadow: none !important;
   cursor: not-allowed !important;
}

.lead-modal .form-select:focus {
   border-color: #00A0C1 !important;
   box-shadow: 0 0 0 0.2rem rgba(0, 160, 193, 0.25) !important;
}

/* Vertex Checkbox Component */
.vertex-checkbox-card {
   background: #f8fafc;
   border: 1px solid #e2e8f0;
   cursor: pointer;
   transition: all 0.2s ease-in-out;
}

.vertex-checkbox-card:hover {
   background: #f1f5f9;
   border-color: #cbd5e1;
}

.vertex-checkbox-card--checked {
   background: rgba(0, 160, 193, 0.08) !important;
   border-color: rgba(0, 160, 193, 0.5) !important;
   box-shadow: 0 1px 3px rgba(0, 160, 193, 0.1);
}

.vertex-checkbox-input {
   width: 18px !important;
   height: 18px !important;
   margin-top: 0 !important;
   cursor: pointer !important;
   border: 1.5px solid #94a3b8 !important;
   border-radius: 4px !important;
   accent-color: #00A0C1 !important;
   transition: all 0.15s ease-in-out !important;
}

.vertex-checkbox-input:checked {
   background-color: #00A0C1 !important;
   border-color: #00A0C1 !important;
}

.vertex-checkbox-input:focus {
   border-color: #00A0C1 !important;
   box-shadow: 0 0 0 0.2rem rgba(0, 160, 193, 0.25) !important;
}
</style>